<template>
  <v-container fluid class="pa-4">
    <v-row>
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title class="uyghur text-h5 pa-4 d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-icon class="mr-2">mdi-account-check</v-icon>
              يوقلىما باشقۇرۇش
              <v-chip class="mr-2 ml-4 uyghur" color="primary" variant="tonal" size="small">
                ئوقۇش يىلى: {{ currentSchoolYear }}
              </v-chip>
            </div>
            <div class="d-flex align-center">
              <v-btn
                v-if="canFinalizeYear"
                color="warning"
                variant="outlined"
                class="uyghur mr-2"
                :loading="finalizingYear"
                @click="confirmFinalizeSchoolYear"
              >
                <v-icon class="mr-2">mdi-calendar-end</v-icon>
                يىل ئاخىرلاشتۇرۇش
              </v-btn>
              <v-btn
                color="error"
                variant="outlined"
                @click="logout"
                class="uyghur"
              >
                <v-icon class="mr-2">mdi-logout</v-icon>
                چىقىش
              </v-btn>
            </div>
          </v-card-title>
          <v-tabs v-model="activeTab" class="uyghur">
            <v-tab value="attendance">
              <v-icon class="mr-2">mdi-calendar-check</v-icon>
              يوقلىما خاتىرىسى
            </v-tab>
            <v-tab value="homework">
              <v-icon class="mr-2">mdi-book-education</v-icon>
              تاپشۇرۇق
            </v-tab>
            <v-tab value="vacation-homework">
              <v-icon class="mr-2">mdi-beach</v-icon>
              تەتىل تاپشۇرۇق
            </v-tab>
            <v-tab value="exam">
              <v-icon class="mr-2">mdi-file-document-edit</v-icon>
              ئىمتىھان
            </v-tab>
            <v-tab value="summary">
              <v-icon class="mr-2">mdi-chart-box</v-icon>
              خۇلاسە
            </v-tab>
            <v-tab value="students">
              <v-icon class="mr-2">mdi-account-group</v-icon>
              ئوقۇغۇچىلار
            </v-tab>
            <v-tab value="wheel">
              <v-icon class="mr-2">mdi-help-circle</v-icon>
              چاقپەلەك
            </v-tab>
          </v-tabs>
        </v-card>
      </v-col>
    </v-row>

    <!-- Attendance Tab Content -->
    <v-window v-model="activeTab">
      <v-window-item value="attendance">

    <!-- Statistics Overview -->
    <v-row>
      <v-col cols="12" md="6" lg="3" v-for="stat in stats" :key="stat.title">
        <v-card class="text-center pa-4" :color="stat.color" dark>
          <v-card-text>
            <v-icon size="48" class="mb-2">{{ stat.icon }}</v-icon>
            <div class="text-h4">{{ stat.value }}</div>
            <div class="uyghur text-subtitle-1">{{ stat.title }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Class Summary View for Grade Manager and Manager - Attendance -->
    <div v-if="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')">
      <!-- Grade Selector for Managers -->
      <v-row class="mt-4">
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedGradeForAttendanceSummary"
            :items="availableGrades"
            item-title="isim"
            item-value="id"
            label="يىللىق تاللاش"
            class="uyghur"
            clearable
            @update:model-value="onGradeSelectedForAttendanceSummary"
          >
            <template v-if="availableGrades.length === 0">
              <v-list-item>
                <v-list-item-title class="uyghur text-grey">سىنىپ يوق</v-list-item-title>
              </v-list-item>
            </template>
          </v-select>
        </v-col>
      </v-row>
      
      <!-- Loading State -->
      <v-row v-if="attendanceTabLoading" class="mt-4">
        <v-col cols="12">
          <v-card>
            <v-card-text class="text-center py-12">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
                width="6"
              ></v-progress-circular>
              <div class="mt-4 uyghur text-h6">يوقلىما خاتىرىسىنى يۈكلەۋاتىدۇ...</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Empty State - Show when no grade selected or no data -->
      <v-row v-else-if="!attendanceTabLoading && (!selectedGradeForAttendanceSummary || classAttendanceSummary.length === 0)" class="mt-4">
        <v-col cols="12">
          <v-card>
            <v-card-text class="text-center py-12">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-information-outline</v-icon>
              <div class="uyghur text-h6 text-grey" v-if="!selectedGradeForAttendanceSummary">
                سىنىپ تاللاڭ
              </div>
              <div class="uyghur text-h6 text-grey" v-else>
                يوقلىما خاتىرىسى يوق
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <!-- Attendance Summary by Class -->
      <v-row v-else-if="!attendanceTabLoading && filteredAttendanceSummary.length > 0" class="mt-4">
        <v-col cols="12">
          <v-card
            v-for="summary in filteredAttendanceSummary"
            :key="summary.class_id"
            class="mb-4"
          >
            <v-card-title class="uyghur">
              <div>
                <div class="text-h6">{{ summary.class_name }}</div>
                <div class="text-subtitle-1 uyghur" v-if="summary.teacher_names.length > 0">
                  ئوقۇتقۇچى: {{ summary.teacher_names.join(', ') }}
                </div>
                <div class="text-subtitle-1 uyghur">
                  جەمئىي ئوقۇغۇچى: {{ summary.total_students }}
                </div>
              </div>
              <v-btn
                :icon="isAttendanceExpanded(summary.class_id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                variant="text"
                @click="toggleAttendanceDetails(summary.class_id)"
                class="uyghur"
              >
                {{ isAttendanceExpanded(summary.class_id) ? 'يوشۇرۇش' : 'تەپسىلات' }}
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="attendanceSummaryHeaders"
                :items="summary.date_summaries"
                class="elevation-0 uyghur"
                :items-per-page="20"
              >
                <template #[`item.date`]="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template #[`item.present_count`]="{ item }">
                  <span class="text-green">{{ item.present_count }}</span>
                </template>
                <template #[`item.late_count`]="{ item }">
                  <span class="text-orange">{{ item.late_count }}</span>
                </template>
                <template #[`item.excused_count`]="{ item }">
                  <span class="text-blue">{{ item.excused_count }}</span>
                </template>
                <template #[`item.absent_count`]="{ item }">
                  <span class="text-red">{{ item.absent_count }}</span>
                </template>
              </v-data-table>
            </v-card-text>
            
            <!-- Expanded Details -->
            <v-expand-transition>
              <div v-show="isAttendanceExpanded(summary.class_id)">
                <v-divider></v-divider>
                <v-card-text>
                  <v-data-table
                    :headers="baseHeaders"
                    :items="getClassAttendanceRecords(summary.class_id)"
                    class="elevation-0 uyghur"
                    :items-per-page="10"
                  >
                    <template #[`item.student_name`]="{ item }">
                      <span class="uyghur">{{ item.student_name }} {{ item.student_family }}</span>
                    </template>
                    <template #[`item.status`]="{ item }">
                      <v-chip
                        :color="getStatusColor(item.status)"
                        :text="getStatusText(item.status)"
                        class="uyghur"
                        size="small"
                      ></v-chip>
                    </template>
                    <template #[`item.date`]="{ item }">
                      {{ formatDate(item.date) }}
                    </template>
                  </v-data-table>
                </v-card-text>
              </div>
            </v-expand-transition>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Filters and Actions -->
    <v-row class="mt-4" v-if="canEdit">
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedClass"
          :items="teacherClasses"
          item-title="display_name"
          item-value="id"
          label="سىنىپ تاللاش"
          class="uyghur"
          clearable
          @update:model-value="onClassSelectedForAttendance"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
          <v-menu
            v-model="attendanceDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="selectedDate"
              label="كۈن تاللاش"
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="props"
              class="uyghur"
              @update:model-value="loadAttendanceData"
            ></v-text-field>
          </template>
          <v-date-picker
            :key="`attendance-${attendanceDatesWithRecords.length}-${attendanceDatesWithRecords.join(',')}`"
            v-model="selectedDate"
            @update:model-value="onDateSelected"
            locale="en"
          >
            <template #day="{ item, props: dayProps }">
              <v-btn
                v-bind="dayProps"
                :class="{ 'highlighted-date-btn': isAttendanceDateHighlighted(item.isoDate) }"
              />
            </template>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" md="4" class="d-flex align-center">
        <v-btn
          color="primary"
          @click="openRecordDialog"
          class="uyghur"
        >
          <v-icon class="mr-2">mdi-plus</v-icon>
          يوقلىما خاتىرىلەش
        </v-btn>
      </v-col>
    </v-row>

    <!-- Attendance Records Table -->
    <v-row v-if="canEdit">
      <v-col cols="12">
        <v-card>
          <v-card-title class="uyghur">
            يوقلىما خاتىرىسى
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="attendanceRecords"
            :loading="loading"
            class="elevation-1 uyghur"
            :items-per-page="20"
          >
            <template #[`item.student_name`]="{ item }">
              <span class="uyghur">{{ item.student_name }} {{ item.student_family }}</span>
            </template>
            <template #[`item.status`]="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                :text="getStatusText(item.status)"
                class="uyghur"
              ></v-chip>
            </template>
            <template #[`item.date`]="{ item }">
              {{ formatDate(item.date) }}
            </template>
            <template #[`item.actions`]="{ item }" v-if="canEdit">
              <v-btn
                icon="mdi-pencil"
                size="small"
                @click="editAttendance(item)"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                size="small"
                color="error"
                @click="deleteAttendance(item.id)"
              ></v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Attendance Summary Table -->
    <v-row v-if="canEdit && groupedAttendanceSummary.length > 0">
      <v-col cols="12">
        <v-card>
          <v-card-title class="uyghur">
            يوقلىما خاتىرىسى جەدۋىلى
          </v-card-title>
          <v-card-text>
            <div class="attendance-summary-table">
              <div v-for="classGroup in groupedAttendanceSummary" :key="classGroup.classId" class="mb-6">
                <h3 class="uyghur mb-3 text-h6" v-if="classGroup.className">
                  {{ classGroup.className }}
                </h3>
                <table v-if="classGroup.dates.length > 0" class="summary-table">
                  <thead>
                    <tr>
                      <th class="student-col uyghur">ئوقۇغۇچى</th>
                      <th v-for="(record, index) in classGroup.dates" :key="index" class="record-col">
                        {{ index + 1 }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="student in classGroup.students" :key="student.id">
                      <td class="student-name uyghur">{{ student.isim }} {{ student.famile }}</td>
                      <td v-for="(record, index) in classGroup.dates" :key="index" class="status-cell">
                        <v-chip
                          v-if="getStudentAttendanceStatus(student.id, record.date)"
                          :color="getStatusColor(getStudentAttendanceStatus(student.id, record.date))"
                          :text="getStatusText(getStudentAttendanceStatus(student.id, record.date))"
                          class="uyghur"
                          size="small"
                        ></v-chip>
                        <span v-else class="no-record">-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="text-center uyghur text-grey pa-4">
                  ھېچقانداق يوقلىما خاتىرىسى يوق
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Record Attendance Dialog -->
    <v-dialog v-model="showRecordDialog" max-width="800px">
      <v-card>
        <v-card-title class="uyghur">
          يوقلىما خاتىرىلەش
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="recordForm.classId"
                  :items="teacherClasses"
                  item-title="display_name"
                  item-value="id"
                  label="سىنىپ"
                  class="uyghur"
                  :rules="[v => !!v || 'سىنىپ تاللاش كېرەك']"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="recordForm.date"
                  type="date"
                  label="كۈن"
                  class="uyghur"
                  :rules="[v => !!v || 'كۈن تاللاش كېرەك']"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            
            <v-divider class="my-4"></v-divider>
            
            <div class="text-h6 uyghur mb-3">ئوقۇغۇچىلار يوقلىما ئەھۋالى</div>
            
            <v-row v-for="student in students" :key="student.id">
              <v-col cols="12" md="4">
                <span class="uyghur">{{ student.isim }} {{ student.famile }}</span>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="studentAttendance[student.id]"
                  :items="statusOptions"
                  item-title="text"
                  item-value="value"
                  label="ھالەت"
                  class="uyghur"
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="studentNotes[student.id]"
                  label="ئىزاھات"
                  class="uyghur"
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
              <v-btn color="grey" @click="closeRecordDialog" class="uyghur">
            بىكار قىلش
          </v-btn>
          <v-btn color="primary" @click="saveAttendance" :disabled="!valid" class="uyghur">
            ساقلاش
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Attendance Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500px">
      <v-card>
        <v-card-title class="uyghur">
          يوقلىما تەھرىرلەش
        </v-card-title>
        <v-card-text>
          <v-form ref="editForm" v-model="editValid">
            <v-select
              v-model="editForm.status"
              :items="statusOptions"
              item-title="text"
              item-value="value"
              label="ھالەت"
              class="uyghur"
              :rules="[v => !!v || 'ھالەت تاللاش كېرەك']"
              required
            ></v-select>
            <v-text-field
              v-model="editForm.notes"
              label="ئىزاھات"
              class="uyghur"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="showEditDialog = false" class="uyghur">
            بىكار قىلش
          </v-btn>
          <v-btn color="primary" @click="updateAttendance" :disabled="!editValid" class="uyghur">
            يېڭىلاش
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      </v-window-item>

      <!-- Homework Tab Content -->
      <v-window-item value="homework">
        <!-- Homework Statistics -->
        <v-row>
          <v-col cols="12" md="6" lg="3" v-for="stat in homeworkStats" :key="stat.title">
            <v-card class="text-center pa-4" :color="stat.color" dark>
              <v-card-text>
                <v-icon size="48" class="mb-2">{{ stat.icon }}</v-icon>
                <div class="text-h4">{{ stat.value }}</div>
                <div class="uyghur text-subtitle-1">{{ stat.title }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Class Summary View for Grade Manager and Manager - Homework -->
        <div v-if="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')">
          <!-- Grade Selector for Managers -->
          <v-row class="mt-4">
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedGradeForHomeworkSummary"
                :items="availableGrades"
                item-title="isim"
                item-value="id"
                label="يىللىق تاللاش"
                class="uyghur"
                clearable
                @update:model-value="onGradeSelectedForHomeworkSummary"
              >
                <template v-if="availableGrades.length === 0">
                  <v-list-item>
                    <v-list-item-title class="uyghur text-grey">سىنىپ يوق</v-list-item-title>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
          
          <!-- Loading State -->
          <v-row v-if="homeworkTabLoading" class="mt-4">
            <v-col cols="12">
              <v-card>
                <v-card-text class="text-center py-12">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                    width="6"
                  ></v-progress-circular>
                  <div class="mt-4 uyghur text-h6">تاپشۇرۇق خاتىرىسىنى يۈكلەۋاتىدۇ...</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          
          <!-- Empty State - Show when no grade selected or no data -->
          <v-row v-else-if="!homeworkTabLoading && (!selectedGradeForHomeworkSummary || classHomeworkSummary.length === 0)" class="mt-4">
            <v-col cols="12">
              <v-card>
                <v-card-text class="text-center py-12">
                  <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-information-outline</v-icon>
                  <div class="uyghur text-h6 text-grey" v-if="!selectedGradeForHomeworkSummary">
                    سىنىپ تاللاڭ
                  </div>
                  <div class="uyghur text-h6 text-grey" v-else>
                    تاپشۇرۇق خاتىرىسى يوق
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          
          <!-- Homework Summary by Class -->
          <v-row v-else-if="!homeworkTabLoading && filteredHomeworkSummary.length > 0" class="mt-4">
            <v-col cols="12">
              <v-card
                v-for="summary in filteredHomeworkSummary"
                :key="summary.class_id"
                class="mb-4"
              >
                <v-card-title class="uyghur">
                  <div>
                    <div class="text-h6">سىنىپ: {{ summary.class_name }}</div>
                    <div class="text-subtitle-1" v-if="summary.teacher_names.length > 0">
                      ئوقۇتقۇچى: {{ summary.teacher_names.join(', ') }}
                    </div>
                    <div class="text-subtitle-1">
                      جەمئىي ئوقۇغۇچى: {{ summary.total_students }}
                    </div>
                  </div>
                  <v-btn
                    :icon="isHomeworkExpanded(summary.class_id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    variant="text"
                    @click="toggleHomeworkDetails(summary.class_id)"
                    class="uyghur"
                  >
                    {{ isHomeworkExpanded(summary.class_id) ? 'يوشۇرۇش' : 'تەپسىلات' }}
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <v-data-table
                    :headers="homeworkSummaryHeaders"
                    :items="summary.date_summaries"
                    class="elevation-0 uyghur"
                    :items-per-page="20"
                  >
                    <template #[`item.date`]="{ item }">
                      {{ formatDate(item.date) }}
                    </template>
                    <template #[`item.homework_count`]="{ item }">
                      <span class="text-green">{{ item.homework_count }}</span>
                    </template>
                    <template #[`item.no_homework_count`]="{ item }">
                      <span class="text-red">{{ item.no_homework_count }}</span>
                    </template>
                  </v-data-table>
                </v-card-text>
                
                <!-- Expanded Details -->
                <v-expand-transition>
                  <div v-show="isHomeworkExpanded(summary.class_id)">
                    <v-divider></v-divider>
                    <v-card-text>
                      <v-data-table
                        :headers="homeworkHeaders"
                        :items="getClassHomeworkRecords(summary.class_id)"
                        class="elevation-0 uyghur"
                        :items-per-page="10"
                      >
                        <template #[`item.student_name`]="{ item }">
                          <span class="uyghur">{{ item.student_name }} {{ item.student_family }}</span>
                        </template>
                        <template #[`item.date`]="{ item }">
                          {{ formatDate(item.date) }}
                        </template>
                        <template #[`item.total_points`]="{ item }">
                          <v-chip
                            :color="getPointsColor(item.total_points)"
                            :text="parseFloat(item.total_points).toFixed(1) + '/0.9'"
                            class="uyghur"
                            size="small"
                          ></v-chip>
                        </template>
                      </v-data-table>
                    </v-card-text>
                  </div>
                </v-expand-transition>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Homework Filters and Actions -->
        <v-row class="mt-4" v-if="canEdit">
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedClassForHomework"
              :items="teacherClasses"
              item-title="display_name"
              item-value="id"
              label="سىنىپ تاللاش"
              class="uyghur"
              clearable
          @update:model-value="onClassSelectedForHomework"
        ></v-select>
      </v-col>
      <v-col cols="12" md="4">
          <v-menu
            v-model="homeworkDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
          <template v-slot:activator="{ props }">
            <v-text-field
              v-model="selectedHomeworkDate"
              label="كۈن تاللاش"
              prepend-inner-icon="mdi-calendar"
              readonly
              v-bind="props"
              class="uyghur"
              @update:model-value="loadHomeworkData"
            ></v-text-field>
          </template>
          <v-date-picker
            :key="`homework-${homeworkDatesWithRecords.length}-${homeworkDatesWithRecords.join(',')}`"
            v-model="selectedHomeworkDate"
            @update:model-value="onHomeworkDateSelected"
            locale="en"
          >
            <template #day="{ item, props: dayProps }">
              <v-btn
                v-bind="dayProps"
                :class="{ 'highlighted-date-btn': isHomeworkDateHighlighted(item.isoDate) }"
              />
            </template>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn
              color="primary"
              @click="openRecordHomeworkDialog"
              class="uyghur"
            >
              <v-icon class="mr-2">mdi-plus</v-icon>
              تاپشۇرۇق خاتىرىلەش
            </v-btn>
          </v-col>
        </v-row>

        <!-- Homework Records Table -->
        <v-row v-if="canEdit">
          <v-col cols="12">
            <v-card>
              <v-card-title class="uyghur">
                تاپشۇرۇق خاتىرىسى
              </v-card-title>
              <v-data-table
                :headers="homeworkHeaders"
                :items="homeworkRecords"
                :loading="homeworkLoading"
                class="elevation-1 uyghur"
                :items-per-page="20"
              >
                <template #[`item.student_name`]="{ item }">
                  <span class="uyghur">{{ item.student_name }} {{ item.student_family }}</span>
                </template>
                <template #[`item.date`]="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                <template v-if="homeworkVisibleFields.new_class_preparation" #[`item.new_class_preparation`]="{ item }">
                  <v-chip
                    :color="item.new_class_preparation ? 'green' : 'grey'"
                    :text="item.new_class_preparation ? 'تاماملاندى' : 'تاماملانمىدى'"
                    class="uyghur"
                    size="small"
                  ></v-chip>
                </template>
                <template v-if="homeworkVisibleFields.main_text_reading" #[`item.main_text_reading`]="{ item }">
                  <v-chip
                    :color="item.main_text_reading ? 'green' : 'grey'"
                    :text="item.main_text_reading ? 'تاماملاندى' : 'تاماملانمىدى'"
                    class="uyghur"
                    size="small"
                  ></v-chip>
                </template>
                <template v-if="homeworkVisibleFields.reading" #[`item.reading`]="{ item }">
                  <v-chip
                    :color="item.reading ? 'green' : 'grey'"
                    :text="item.reading ? 'تاماملاندى' : 'تاماملانمىدى'"
                    class="uyghur"
                    size="small"
                  ></v-chip>
                </template>
                <template v-if="homeworkVisibleFields.exercise" #[`item.exercise`]="{ item }">
                  <v-chip
                    :color="item.exercise ? 'green' : 'grey'"
                    :text="item.exercise ? 'تاماملاندى' : 'تاماملانمىدى'"
                    class="uyghur"
                    size="small"
                  ></v-chip>
                </template>
                <template v-if="homeworkVisibleFields.spelling" #[`item.spelling`]="{ item }">
                  <v-chip
                    :color="item.spelling ? 'green' : 'grey'"
                    :text="item.spelling ? 'تاماملاندى' : 'تاماملانمىدى'"
                    class="uyghur"
                    size="small"
                  ></v-chip>
                </template>
                <template #[`item.total_points`]="{ item }">
                  <v-chip
                    :color="getPointsColor(item.total_points)"
                    :text="parseFloat(item.total_points).toFixed(1) + '/0.9'"
                    class="uyghur"
                    size="small"
                  ></v-chip>
                </template>
                <template #[`item.actions`]="{ item }" v-if="canEdit">
                  <v-btn
                    icon="mdi-pencil"
                    size="small"
                    @click="editHomework(item)"
                  ></v-btn>
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    color="error"
                    @click="deleteHomework(item.id)"
                  ></v-btn>
                </template>
              </v-data-table>
        </v-card>
      </v-col>
    </v-row>

        <!-- Homework Summary Table -->
        <v-row v-if="canEdit && groupedHomeworkSummary.length > 0">
          <v-col cols="12">
            <v-card>
              <v-card-title class="uyghur">
                تاپشۇرۇق خاتىرىسى جەدۋىلى
              </v-card-title>
              <v-card-text>
                <div class="homework-summary-table">
                  <div v-for="classGroup in groupedHomeworkSummary" :key="classGroup.classId" class="mb-6">
                    <h3 class="uyghur mb-3 text-h6" v-if="classGroup.className">
                      {{ classGroup.className }}
                    </h3>
                    <table v-if="classGroup.dates.length > 0" class="summary-table">
                      <thead>
                        <tr>
                          <th class="student-col uyghur">ئوقۇغۇچى</th>
                          <th v-for="(record, index) in classGroup.dates" :key="index" class="record-col">
                            {{ index + 1 }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="student in classGroup.students" :key="student.id">
                          <td class="student-name uyghur">{{ student.isim }} {{ student.famile }}</td>
                          <td v-for="(record, index) in classGroup.dates" :key="index" class="points-cell">
                            <v-chip
                              v-if="getStudentHomeworkPoints(student.id, record.date)"
                              :color="getPointsColor(getStudentHomeworkPoints(student.id, record.date))"
                              :text="parseFloat(getStudentHomeworkPoints(student.id, record.date)).toFixed(1)"
                              class="uyghur"
                              size="small"
                            ></v-chip>
                            <span v-else class="no-record">-</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-else class="text-center uyghur text-grey pa-4">
                      ھېچقانداق تاپشۇرۇق خاتىرىسى يوق
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Record Homework Dialog -->
        <v-dialog v-model="showRecordHomeworkDialog" max-width="800px">
          <v-card>
            <v-card-title class="uyghur">
              تاپشۇرۇق خاتىرىلەش
            </v-card-title>
            <v-card-text>
              <v-form ref="homeworkForm" v-model="homeworkFormValid">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="homeworkForm.classId"
                      :items="teacherClasses"
                      item-title="display_name"
                      item-value="id"
                      label="سىنىپ تاللاش"
                      class="uyghur"
                      :rules="[v => !!v || 'سىنىپ تاللاش كېرەك']"
                      required
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="homeworkForm.date"
                      type="date"
                      label="كۈن"
                      class="uyghur"
                      :rules="[v => !!v || 'كۈن تاللاش كېرەك']"
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
                
                <!-- Students List with Homework Checkboxes -->
                <v-row v-if="homeworkForm.classId">
                  <v-col cols="12">
                    <v-card>
                      <v-card-title class="uyghur">ئوقۇغۇچىلار</v-card-title>
                      <v-card-subtitle class="pa-4">
                        <div class="d-flex flex-wrap align-center ga-2">
                          <span class="uyghur text-subtitle-2">ھەممىسى:</span>
                          <v-checkbox
                            v-if="homeworkVisibleFields.new_class_preparation"
                            :model-value="isHomeworkFieldAllSelected('new_class_preparation')"
                            :indeterminate="isHomeworkFieldIndeterminate('new_class_preparation')"
                            :label="`${homeworkFieldLabels.new_class_preparation} (${homeworkPointValues.new_class_preparation})`"
                            density="compact"
                            hide-details
                            class="uyghur"
                            @update:model-value="toggleHomeworkFieldForAll('new_class_preparation', $event)"
                          ></v-checkbox>
                          <v-checkbox
                            v-if="homeworkVisibleFields.main_text_reading"
                            :model-value="isHomeworkFieldAllSelected('main_text_reading')"
                            :indeterminate="isHomeworkFieldIndeterminate('main_text_reading')"
                            :label="`${homeworkFieldLabels.main_text_reading} (${homeworkPointValues.main_text_reading})`"
                            density="compact"
                            hide-details
                            class="uyghur"
                            @update:model-value="toggleHomeworkFieldForAll('main_text_reading', $event)"
                          ></v-checkbox>
                          <v-checkbox
                            v-if="homeworkVisibleFields.reading"
                            :model-value="isHomeworkFieldAllSelected('reading')"
                            :indeterminate="isHomeworkFieldIndeterminate('reading')"
                            :label="`${homeworkFieldLabels.reading} (${homeworkPointValues.reading})`"
                            density="compact"
                            hide-details
                            class="uyghur"
                            @update:model-value="toggleHomeworkFieldForAll('reading', $event)"
                          ></v-checkbox>
                          <v-checkbox
                            v-if="homeworkVisibleFields.exercise"
                            :model-value="isHomeworkFieldAllSelected('exercise')"
                            :indeterminate="isHomeworkFieldIndeterminate('exercise')"
                            :label="`${homeworkFieldLabels.exercise} (${homeworkPointValues.exercise})`"
                            density="compact"
                            hide-details
                            class="uyghur"
                            @update:model-value="toggleHomeworkFieldForAll('exercise', $event)"
                          ></v-checkbox>
                          <v-checkbox
                            v-if="homeworkVisibleFields.spelling"
                            :model-value="isHomeworkFieldAllSelected('spelling')"
                            :indeterminate="isHomeworkFieldIndeterminate('spelling')"
                            :label="`${homeworkFieldLabels.spelling} (${homeworkPointValues.spelling})`"
                            density="compact"
                            hide-details
                            class="uyghur"
                            @update:model-value="toggleHomeworkFieldForAll('spelling', $event)"
                          ></v-checkbox>
                          <v-spacer></v-spacer>
                          <v-btn
                            size="small"
                            variant="outlined"
                            color="primary"
                            class="uyghur"
                            @click="selectAllHomeworkForAllStudents"
                          >
                            ھەممىسىنى تاللاش
                          </v-btn>
                          <v-btn
                            size="small"
                            variant="outlined"
                            color="grey"
                            class="uyghur"
                            @click="clearAllHomeworkForAllStudents"
                          >
                            بىكار قىلىش
                          </v-btn>
                        </div>
                      </v-card-subtitle>
                      <v-card-text>
                        <div v-for="student in getStudentsForClass(homeworkForm.classId)" :key="student.id" class="mb-4">
                          <v-card variant="outlined">
                            <v-card-title class="uyghur text-subtitle-1 d-flex align-center flex-wrap">
                              <span>{{ student.isim }} {{ student.famile }}</span>
                              <v-spacer></v-spacer>
                              <v-checkbox
                                :model-value="isStudentHomeworkAllSelected(student.id)"
                                label="ھەممىسى"
                                density="compact"
                                hide-details
                                class="uyghur"
                                @update:model-value="toggleSelectAllForStudent(student.id, $event)"
                              ></v-checkbox>
                            </v-card-title>
                            <v-card-text>
                              <v-row>
                                <v-col cols="12" md="6">
                                  <v-checkbox
                                    v-if="homeworkVisibleFields.new_class_preparation"
                                    v-model="homeworkForm.students[student.id].new_class_preparation"
                                    :label="`${homeworkFieldLabels.new_class_preparation} (${homeworkPointValues.new_class_preparation})`"
                                    class="uyghur"
                                    @change="updateStudentPoints(student.id)"
                                  ></v-checkbox>
                                  <v-checkbox
                                    v-if="homeworkVisibleFields.main_text_reading"
                                    v-model="homeworkForm.students[student.id].main_text_reading"
                                    :label="`${homeworkFieldLabels.main_text_reading} (${homeworkPointValues.main_text_reading})`"
                                    class="uyghur"
                                    @change="updateStudentPoints(student.id)"
                                  ></v-checkbox>
                                  <v-checkbox
                                    v-if="homeworkVisibleFields.reading"
                                    v-model="homeworkForm.students[student.id].reading"
                                    :label="`${homeworkFieldLabels.reading} (${homeworkPointValues.reading})`"
                                    class="uyghur"
                                    @change="updateStudentPoints(student.id)"
                                  ></v-checkbox>
                                </v-col>
                                <v-col cols="12" md="6">
                                  <v-checkbox
                                    v-if="homeworkVisibleFields.exercise"
                                    v-model="homeworkForm.students[student.id].exercise"
                                    :label="`${homeworkFieldLabels.exercise} (${homeworkPointValues.exercise})`"
                                    class="uyghur"
                                    @change="updateStudentPoints(student.id)"
                                  ></v-checkbox>
                                  <v-checkbox
                                    v-if="homeworkVisibleFields.spelling"
                                    v-model="homeworkForm.students[student.id].spelling"
                                    :label="`${homeworkFieldLabels.spelling} (${homeworkPointValues.spelling})`"
                                    class="uyghur"
                                    @change="updateStudentPoints(student.id)"
                                  ></v-checkbox>
                                  <v-text-field
                                    v-model="homeworkForm.students[student.id].notes"
                                    label="ئىزاھات"
                                    class="uyghur"
                                    variant="outlined"
                                    density="compact"
                                  ></v-text-field>
                                </v-col>
                              </v-row>
                              <v-divider class="mt-2"></v-divider>
                              <div class="mt-2">
                                <v-chip
                                  :color="getPointsColor(homeworkForm.students[student.id].total_points)"
                                  :text="`جەمئىي: ${parseFloat(homeworkForm.students[student.id].total_points).toFixed(1)}/0.9`"
                                  class="uyghur"
                                ></v-chip>
                              </div>
                            </v-card-text>
                          </v-card>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" @click="closeRecordHomeworkDialog" class="uyghur">
                بىكار قىلش
              </v-btn>
              <v-btn color="primary" @click="saveHomework" :disabled="!homeworkFormValid" class="uyghur">
                ساقلاش
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Edit Homework Dialog -->
        <v-dialog v-model="showEditHomeworkDialog" max-width="600px">
          <v-card>
            <v-card-title class="uyghur">
              تاپشۇرۇق تەھرىرلەش
            </v-card-title>
            <v-card-text>
              <v-form ref="editHomeworkForm" v-model="editHomeworkFormValid">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-checkbox
                      v-if="homeworkVisibleFields.new_class_preparation"
                      v-model="editHomeworkForm.new_class_preparation"
                      :label="`${homeworkFieldLabels.new_class_preparation} (${homeworkPointValues.new_class_preparation})`"
                      class="uyghur"
                      @change="updateEditPoints"
                    ></v-checkbox>
                    <v-checkbox
                      v-if="homeworkVisibleFields.main_text_reading"
                      v-model="editHomeworkForm.main_text_reading"
                      :label="`${homeworkFieldLabels.main_text_reading} (${homeworkPointValues.main_text_reading})`"
                      class="uyghur"
                      @change="updateEditPoints"
                    ></v-checkbox>
                    <v-checkbox
                      v-if="homeworkVisibleFields.reading"
                      v-model="editHomeworkForm.reading"
                      :label="`${homeworkFieldLabels.reading} (${homeworkPointValues.reading})`"
                      class="uyghur"
                      @change="updateEditPoints"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-checkbox
                      v-if="homeworkVisibleFields.exercise"
                      v-model="editHomeworkForm.exercise"
                      :label="`${homeworkFieldLabels.exercise} (${homeworkPointValues.exercise})`"
                      class="uyghur"
                      @change="updateEditPoints"
                    ></v-checkbox>
                    <v-checkbox
                      v-if="homeworkVisibleFields.spelling"
                      v-model="editHomeworkForm.spelling"
                      :label="`${homeworkFieldLabels.spelling} (${homeworkPointValues.spelling})`"
                      class="uyghur"
                      @change="updateEditPoints"
                    ></v-checkbox>
                    <v-text-field
                      v-model="editHomeworkForm.notes"
                      label="ئىزاھات"
                      class="uyghur"
                      variant="outlined"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-chip
                      :color="getPointsColor(editHomeworkForm.total_points)"
                      :text="`جەمئىي: ${parseFloat(editHomeworkForm.total_points).toFixed(1)}/0.9`"
                      class="uyghur"
                    ></v-chip>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey" @click="showEditHomeworkDialog = false" class="uyghur">
                بىكار قىلش
              </v-btn>
              <v-btn color="primary" @click="updateHomework" :disabled="!editHomeworkFormValid" class="uyghur">
            يېڭىلاش
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      </v-window-item>

      <!-- Vacation Homework Tab -->
      <v-window-item value="vacation-homework">
        <v-row v-if="canEdit">
          <v-col cols="12" md="6" lg="3" v-for="stat in vacationStats" :key="stat.title">
            <v-card class="text-center pa-4" :color="stat.color" dark>
              <v-card-text>
                <v-icon size="48" class="mb-2">{{ stat.icon }}</v-icon>
                <div class="text-h4">{{ stat.value }}</div>
                <div class="uyghur text-subtitle-1">{{ stat.title }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4" v-if="canEdit">
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedClassForVacation"
              :items="teacherClasses"
              item-title="display_name"
              item-value="id"
              label="سىنىپ تاللاش"
              class="uyghur"
              clearable
              @update:model-value="loadVacationData"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn
              color="primary"
              :disabled="!selectedClassForVacation || vacationLoading"
              :loading="vacationSaving"
              @click="saveVacationScores"
              class="uyghur"
            >
              <v-icon class="mr-2">mdi-content-save</v-icon>
              ساقلاش
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="canEdit && selectedClassForVacation">
          <v-col cols="12">
            <v-card>
              <v-card-title class="uyghur">تەتىل تاپشۇرۇق نۇقتىسى (0-20)</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="vacationHeaders"
                  :items="vacationStudentScores"
                  :loading="vacationLoading"
                  class="elevation-0 uyghur"
                  :items-per-page="50"
                >
                  <template #[`item.student_name`]="{ item }">
                    <span class="uyghur">{{ item.student_name }} {{ item.student_family }}</span>
                  </template>
                  <template #[`item.score`]="{ item }">
                    <v-text-field
                      v-model.number="item.score"
                      type="number"
                      min="0"
                      max="20"
                      step="0.1"
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="uyghur"
                    ></v-text-field>
                  </template>
                  <template #[`item.notes`]="{ item }">
                    <v-text-field
                      v-model="item.notes"
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="uyghur"
                    ></v-text-field>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Exam Tab -->
      <v-window-item value="exam">
        <v-row v-if="canEdit">
          <v-col cols="12" md="6" lg="3" v-for="stat in examStats" :key="stat.title">
            <v-card class="text-center pa-4" :color="stat.color" dark>
              <v-card-text>
                <v-icon size="48" class="mb-2">{{ stat.icon }}</v-icon>
                <div class="text-h4">{{ stat.value }}</div>
                <div class="uyghur text-subtitle-1">{{ stat.title }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-4" v-if="canEdit">
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedClassForExam"
              :items="teacherClasses"
              item-title="display_name"
              item-value="id"
              label="سىنىپ تاللاش"
              class="uyghur"
              clearable
              @update:model-value="loadExamData"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn
              color="primary"
              :disabled="!selectedClassForExam || examLoading"
              :loading="examSaving"
              @click="saveExamScores"
              class="uyghur"
            >
              <v-icon class="mr-2">mdi-content-save</v-icon>
              ساقلاش
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="canEdit && selectedClassForExam">
          <v-col cols="12">
            <v-card>
              <v-card-title class="uyghur">ئىمتىھان نۇقتىسى (0-100)</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="examHeaders"
                  :items="examStudentScores"
                  :loading="examLoading"
                  class="elevation-0 uyghur"
                  :items-per-page="50"
                >
                  <template #[`item.student_name`]="{ item }">
                    <span class="uyghur">{{ item.student_name }} {{ item.student_family }}</span>
                  </template>
                  <template #[`item.score`]="{ item }">
                    <v-text-field
                      v-model.number="item.score"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="uyghur"
                    ></v-text-field>
                  </template>
                  <template #[`item.notes`]="{ item }">
                    <v-text-field
                      v-model="item.notes"
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="uyghur"
                    ></v-text-field>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Summary Tab -->
      <v-window-item value="summary">
        <v-row class="mt-4">
          <v-col cols="12" md="4" v-if="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')">
            <v-select
              v-model="selectedGradeForSummary"
              :items="availableGrades"
              item-title="isim"
              item-value="id"
              label="يىللىق سىنىپ تاللاش"
              class="uyghur"
              clearable
              @update:model-value="onGradeSelectedForSummary"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedClassForSummary"
              :items="filteredClassesForSummary"
              item-title="display_name"
              item-value="id"
              label="سىنىپ تاللاش"
              class="uyghur"
              clearable
              :disabled="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager') && !selectedGradeForSummary"
              @update:model-value="loadSummaryData"
            ></v-select>
          </v-col>
        </v-row>

        <v-row v-if="summaryLoading">
          <v-col cols="12" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-col>
        </v-row>

        <v-row v-else-if="summaryData.length > 0">
          <v-col cols="12">
            <v-card>
              <v-card-title class="uyghur">ئوقۇغۇچى خۇلاسە نۇقتىسى (100)</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="summaryHeaders"
                  :items="summaryData"
                  class="elevation-0 uyghur"
                  :items-per-page="50"
                >
                  <template #[`item.student_name`]="{ item }">
                    <span class="uyghur">{{ item.student_name }} {{ item.student_family }}</span>
                  </template>
                  <template #[`item.attendance_score`]="{ item }">
                    {{ formatScore(item.attendance_score) }} / {{ formatScore(item.max_attendance_score) }}
                  </template>
                  <template #[`item.vacation_score`]="{ item }">
                    {{ formatScore(item.vacation_score) }} / {{ formatScore(item.max_vacation_score) }}
                  </template>
                  <template #[`item.homework_score`]="{ item }">
                    {{ formatScore(item.homework_score) }} / {{ formatScore(item.max_homework_score) }}
                  </template>
                  <template #[`item.exam_score`]="{ item }">
                    {{ formatScore(item.exam_score) }} / {{ formatScore(item.max_exam_score) }}
                  </template>
                  <template #[`item.total_score`]="{ item }">
                    <v-chip :color="getSummaryColor(item.total_score)" size="small" class="uyghur">
                      {{ formatScore(item.total_score) }} / {{ formatScore(item.max_total_score) }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-else-if="!summaryLoading">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-8">
                <div class="uyghur text-h6 text-grey">سىنىپ تاللاپ خۇلاسە كۆرۈڭ</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Students Tab Content -->
      <v-window-item value="students">
        <!-- Students Statistics (only for teachers) -->
        <v-row v-if="canEdit">
          <v-col cols="12" md="6" lg="3" v-for="stat in studentStats" :key="stat.title">
            <v-card class="text-center pa-4" :color="stat.color" dark>
              <v-card-text>
                <v-icon size="48" class="mb-2">{{ stat.icon }}</v-icon>
                <div class="text-h4">{{ stat.value }}</div>
                <div class="uyghur text-subtitle-1">{{ stat.title }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Students Actions -->
        <v-row class="mt-4">
          <!-- Grade Selector (for managers) -->
          <v-col cols="12" md="4" v-if="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')">
            <v-select
              v-model="selectedGradeForStudents"
              :items="availableGrades"
              item-title="isim"
              item-value="id"
              label="يىللىق تاللاش"
              class="uyghur"
              clearable
              @update:model-value="onGradeSelectedForStudents"
            >
              <template v-if="availableGrades.length === 0">
                <v-list-item>
                  <v-list-item-title class="uyghur text-grey">سىنىپ يوق</v-list-item-title>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
          <!-- Class Selector (always visible) -->
          <v-col cols="12" :md="(!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')) ? 4 : (canEdit ? 4 : 12)">
            <v-select
              v-model="selectedClassForStudents"
              :items="filteredClassesForStudents"
              item-title="display_name"
              item-value="id"
              label="سىنىپ تاللاش"
              class="uyghur"
              clearable
              :disabled="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager') && !selectedGradeForStudents"
              :placeholder="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager') && !selectedGradeForStudents ? 'ئاۋۋال سىنىپ تاللاڭ' : ''"
              @update:model-value="loadStudentsForClass"
            >
              <template v-if="filteredClassesForStudents.length === 0 && !canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')">
                <v-list-item>
                  <v-list-item-title class="uyghur text-grey">ئاۋۋال سىنىپ تاللاڭ</v-list-item-title>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
          <!-- Add Student Button (only for teachers) -->
          <v-col cols="12" md="4" class="d-flex align-center" v-if="canEdit">
            <v-btn
              color="primary"
              @click="showAddStudentDialog = true"
              :disabled="!selectedClassForStudents"
              class="uyghur"
            >
              <v-icon class="mr-2">mdi-plus</v-icon>
              ئوقۇغۇچى قوشۇش
            </v-btn>
          </v-col>
        </v-row>

        <!-- Students Table (only show when class is selected) -->
        <v-row v-if="selectedClassForStudents">
          <v-col cols="12">
            <v-card>
              <v-card-title class="uyghur">
                ئوقۇغۇچىلار تىزىملىكى
              </v-card-title>
              <v-data-table
                :headers="studentHeaders"
                :items="filteredStudents"
                :loading="loading"
                class="elevation-1 uyghur"
                :items-per-page="20"
              >
                <template #[`item.row_number`]="{ index }">
                  {{ index + 1 }}
                </template>
                <template #[`item.full_name`]="{ item }">
                  <span class="uyghur">{{ item.isim }} {{ item.famile }}</span>
                </template>
                <template #[`item.class_name`]="{ item }">
                  <span class="uyghur">{{ item.class_name || 'سىنىپ يوق' }}</span>
                </template>
                <template #[`item.phone1`]="{ item }">
                  <span style="direction: ltr; text-align: left; display: inline-block; width: 100%;">{{ item.phone1 || '-' }}</span>
                </template>
                <template #[`item.phone2`]="{ item }">
                  <span style="direction: ltr; text-align: left; display: inline-block; width: 100%;">{{ item.phone2 || '-' }}</span>
                </template>
                <template #[`item.actions`]="{ item }" v-if="canEdit">
                  <v-btn
                    icon="mdi-pencil"
                    size="small"
                    @click="editStudent(item)"
                  ></v-btn>
                  <v-btn
                    icon="mdi-delete"
                    size="small"
                    color="error"
                    @click="deleteStudent(item.id)"
                  ></v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
        
        <!-- Message when no class selected -->
        <v-row v-else class="mt-4">
          <v-col cols="12">
            <v-card>
              <v-card-text class="text-center py-12">
                <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-school-outline</v-icon>
                <div class="uyghur text-h6 text-grey">سىنىپ تاللاڭ</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Wheel Tab Content -->
      <v-window-item value="wheel">
        <v-row>
          <v-col cols="12" md="8">
            <v-card class="pa-4">
              <v-card-title class="uyghur text-h5 mb-4">
                <v-icon class="mr-2">mdi-help-circle</v-icon>
                چاقپەلەك
              </v-card-title>
              
              <!-- Grade and Class Selection -->
              <v-row class="mb-4">
                <!-- Grade Selector (for managers) -->
                <v-col cols="12" md="6" v-if="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')">
                  <v-select
                    v-model="selectedGradeForWheel"
                    :items="availableGrades"
                    item-title="isim"
                    item-value="id"
                    label="يىللىق تاللاش"
                    class="uyghur"
                    clearable
                    @update:model-value="onGradeSelectedForWheel"
                  >
                    <template v-if="availableGrades.length === 0">
                      <v-list-item>
                        <v-list-item-title class="uyghur text-grey">سىنىپ يوق</v-list-item-title>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
                <!-- Class Selector -->
                <v-col cols="12" :md="(!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')) ? 6 : 6">
                  <v-select
                    v-model="selectedClassForWheel"
                    :items="filteredClassesForWheel"
                    item-title="display_name"
                    item-value="id"
                    label="سىنىپ تاللاش"
                    class="uyghur"
                    clearable
                    :disabled="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager') && !selectedGradeForWheel"
                    :placeholder="!canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager') && !selectedGradeForWheel ? 'ئاۋۋال سىنىپ تاللاڭ' : ''"
                    @update:model-value="loadWheelStudents"
                  >
                    <template v-if="filteredClassesForWheel.length === 0 && !canEdit && (teacherRole === 'grade_manager' || teacherRole === 'manager')">
                      <v-list-item>
                        <v-list-item-title class="uyghur text-grey">ئاۋۋال سىنىپ تاللاڭ</v-list-item-title>
                      </v-list-item>
                    </template>
              </v-select>
            </v-col>
          </v-row>
              
              <!-- Show message if no class selected -->
              <template v-if="!selectedClassForWheel">
                <div class="text-center py-12">
                  <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-school-outline</v-icon>
                  <div class="uyghur text-h6 text-grey">سىنىپ تاللاڭ</div>
                </div>
              </template>
              
              <!-- Wheel Content (only show when class is selected) -->
              <template v-else>
                <div class="picker-container text-center mb-6">
                  <div v-if="wheelStudents.length === 0" class="wheel-empty uyghur">
                    {{ currentDisplayName }}
                  </div>

                  <div v-else class="wheel-stage">
                    <div class="wheel-pointer" aria-hidden="true"></div>

                    <div
                      class="wheel-disk"
                      :class="{ spinning: isSwitching, 'no-transition': wheelSnapRotation }"
                      :style="{
                        transform: `rotate(${wheelRotation}deg)`,
                        transitionDuration: `${activeWheelSpinDurationMs}ms`
                      }"
                      @click="pickStudent"
                    >
                      <svg class="wheel-svg" viewBox="0 0 100 100">
                        <g v-for="segment in wheelSegments" :key="segment.id">
                          <path
                            :d="segment.path"
                            :fill="segment.color"
                            stroke="#fff"
                            stroke-width="0.35"
                          />
                          <text
                            v-if="wheelStudents.length <= 24"
                            :x="segment.labelX"
                            :y="segment.labelY"
                            :transform="`rotate(${segment.labelAngle}, ${segment.labelX}, ${segment.labelY})`"
                            text-anchor="middle"
                            dominant-baseline="middle"
                            class="wheel-label uyghur"
                            :font-size="segment.fontSize"
                          >{{ segment.label }}</text>
                        </g>
                        <circle cx="50" cy="50" r="12" class="wheel-hub-ring" />
                      </svg>
                    </div>

                    <button
                      type="button"
                      class="wheel-center uyghur"
                      :disabled="isSwitching || wheelStudents.length === 0"
                      @click.stop="pickStudent"
                    >
                      <span v-if="isSwitching">...</span>
                      <span v-else>ئايلاندۇر</span>
                    </button>
                  </div>

                  <div class="wheel-status uyghur mt-4">
                    {{ currentDisplayName }}
                  </div>

                  <div class="wheel-instant-option mt-3">
                    <v-checkbox
                      v-model="wheelInstant"
                      label="دەرھال"
                      class="uyghur"
                      density="compact"
                      hide-details
                      color="primary"
                      :disabled="isSwitching"
                    ></v-checkbox>
                  </div>
                </div>

                <!-- Selected Student Display -->
                <div v-if="selectedStudent" class="text-center mb-4">
                  <v-card color="success" dark class="pa-4 winner-card">
                    <v-card-title class="uyghur text-h4">
                      <v-icon class="mr-2">mdi-star</v-icon>
                      تاللانغان ئوقۇغۇچى
                    </v-card-title>
                    <v-card-text class="text-h5 uyghur">
                      {{ selectedStudent.isim }} {{ selectedStudent.famile }}
                    </v-card-text>
                  </v-card>
                </div>
              </template>
            </v-card>
          </v-col>
          
          <v-col cols="12" md="4" v-if="selectedClassForWheel">
            <v-card class="pa-4">
              <v-card-title class="uyghur text-h6 mb-4">
                <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                تاللانغان ئوقۇغۇچىلار
              </v-card-title>
              
              <div v-if="pickedStudents.length === 0" class="text-center text-grey uyghur">
                ھېچقانداق ئوقۇغۇچى تاللانمىدى
              </div>
              
              <v-list v-else>
                <v-list-item
                  v-for="(student, index) in pickedStudents"
                  :key="student.id"
                  class="uyghur"
                >
                  <template v-slot:prepend>
                    <v-chip color="primary" size="small">{{ index + 1 }}</v-chip>
                  </template>
                  
                  <v-list-item-title class="uyghur">
                    {{ student.isim }} {{ student.famile }}
                  </v-list-item-title>
                  
                  <template v-slot:append>
                    <v-btn
                      icon="mdi-delete"
                      size="small"
                      color="error"
                      @click="removePickedStudent(student.id)"
                    ></v-btn>
                  </template>
                </v-list-item>
              </v-list>
              
              <v-divider class="my-4"></v-divider>
              
              <div class="text-center">
                <v-btn
                  color="error"
                  variant="outlined"
                  @click="clearPickedStudents"
                  :disabled="pickedStudents.length === 0"
                  class="uyghur"
                >
                  <v-icon class="mr-2">mdi-delete-sweep</v-icon>
                  تازىلاش
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>
    </v-window>

    <!-- Add Student Dialog -->
    <v-dialog v-model="showAddStudentDialog" max-width="600px">
      <v-card>
        <v-card-title class="uyghur">
          ئوقۇغۇچى قوشۇش - {{ getSelectedClassName() }}
        </v-card-title>
        <v-card-text>
          <v-form ref="studentForm" v-model="studentFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="studentForm.isim"
                  label="ئىسمى"
                  class="uyghur"
                  :rules="[v => !!v || 'ئىسمى كېرەك']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="studentForm.famile"
                  label="فامىلىسى"
                  class="uyghur"
                  :rules="[v => !!v || 'فامىلىسى كېرەك']"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="studentForm.phone1"
                  label="تېلېفون نومۇرى 1"
                  class="uyghur phone-ltr"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="studentForm.phone2"
                  label="تېلېفون نومۇرى 2"
                  class="uyghur phone-ltr"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="studentForm.turushluq_doliti"
                  label="تۇرۇشلۇق دۆلەت"
                  class="uyghur"
                  :rules="[v => !!v || 'تۇرۇشلۇق دۆلەت كېرەك']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="studentForm.tughulghan_waqti"
                  type="date"
                  label="تۇغۇلغان ۋاقتى"
                  class="uyghur"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="studentForm.sort_order"
                  label="تەرتىپ نومۇرى"
                  type="number"
                  class="uyghur"
                  :rules="[v => v >= 0 || 'تەرتىپ نومۇرى 0 دىن چوڭ بولۇشى كېرەك']"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="showAddStudentDialog = false" class="uyghur">
            بىكار قىلش
          </v-btn>
          <v-btn color="primary" @click="saveStudent" :disabled="!studentFormValid" class="uyghur">
            ساقلاش
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Student Dialog -->
    <v-dialog v-model="showEditStudentDialog" max-width="600px">
      <v-card>
        <v-card-title class="uyghur">
          ئوقۇغۇچى تەھرىرلەش
        </v-card-title>
        <v-card-text>
          <v-form ref="editStudentForm" v-model="editStudentFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editStudentForm.isim"
                  label="ئىسمى"
                  class="uyghur"
                  :rules="[v => !!v || 'ئىسمى كېرەك']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editStudentForm.famile"
                  label="فامىلىسى"
                  class="uyghur"
                  :rules="[v => !!v || 'فامىلىسى كېرەك']"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editStudentForm.phone1"
                  label="تېلېفون نومۇرى 1"
                  class="uyghur phone-ltr"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editStudentForm.phone2"
                  label="تېلېفون نومۇرى 2"
                  class="uyghur phone-ltr"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editStudentForm.turushluq_doliti"
                  label="تۇرۇشلۇق دۆلەت"
                  class="uyghur"
                  :rules="[v => !!v || 'تۇرۇشلۇق دۆلەت كېرەك']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editStudentForm.tughulghan_waqti"
                  type="date"
                  label="تۇغۇلغان ۋاقتى"
                  class="uyghur"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editStudentForm.sort_order"
                  label="تەرتىپ نومۇرى"
                  type="number"
                  class="uyghur"
                  :rules="[v => v >= 0 || 'تەرتىپ نومۇرى 0 دىن چوڭ بولۇشى كېرەك']"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="editStudentForm.sinip"
                  :items="teacherClasses"
                  item-title="display_name"
                  item-value="id"
                  label="سىنىپ"
                  class="uyghur"
                  :rules="[v => !!v || 'سىنىپ تاللاش كېرەك']"
                  required
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="showEditStudentDialog = false" class="uyghur">
            بىكار قىلش
          </v-btn>
          <v-btn color="primary" @click="updateStudent" :disabled="!editStudentFormValid" class="uyghur">
            يېڭىلاش
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      <span class="uyghur">{{ snackbar.message }}</span>
    </v-snackbar>
  </v-container>
</template>

<script>
import attendanceApi from '@/services/attendanceApi'

export default {
  name: 'AttendanceView',
  data() {
    return {
      loading: false,
      valid: false,
      editValid: false,
      showRecordDialog: false,
      showEditDialog: false,
      
      // Tab management
      activeTab: 'attendance',
      
      // Student management
      showAddStudentDialog: false,
      showEditStudentDialog: false,
      studentFormValid: false,
      editStudentFormValid: false,
      selectedClassForStudents: null,
      selectedGradeForStudents: null,
      selectedGradeForAttendanceSummary: null,
      teacherClasses: [],
      availableGrades: [],
      
      // Wheel functionality
      selectedClassForWheel: null,
      selectedGradeForWheel: null,
      selectedGradeForHomeworkSummary: null,
      selectedGradeForSummary: null,
      selectedClassForVacation: null,
      selectedClassForExam: null,
      selectedClassForSummary: null,
      vacationStudentScores: [],
      examStudentScores: [],
      summaryData: [],
      vacationLoading: false,
      vacationSaving: false,
      examLoading: false,
      examSaving: false,
      summaryLoading: false,
      vacationStats: [
        { title: 'جەمئىي ئوقۇغۇچى', value: 0, icon: 'mdi-account-group', color: 'blue' },
        { title: 'نومۇر بېرىلگەن', value: 0, icon: 'mdi-check-circle', color: 'green' },
        { title: 'نومۇر بېرىلمىگەن', value: 0, icon: 'mdi-close-circle', color: 'red' },
        { title: 'ئوتتۇرىچە', value: 0, icon: 'mdi-chart-line', color: 'orange' }
      ],
      examStats: [
        { title: 'جەمئىي ئوقۇغۇچى', value: 0, icon: 'mdi-account-group', color: 'blue' },
        { title: 'نومۇر بېرىلگەن', value: 0, icon: 'mdi-check-circle', color: 'green' },
        { title: 'نومۇر بېرىلمىگەن', value: 0, icon: 'mdi-close-circle', color: 'red' },
        { title: 'ئوتتۇرىچە', value: 0, icon: 'mdi-chart-line', color: 'orange' }
      ],
      vacationHeaders: [
        { title: 'ئوقۇغۇچى', key: 'student_name', sortable: true },
        { title: 'نومۇر (0-20)', key: 'score', sortable: true },
        { title: 'ئىزاھات', key: 'notes', sortable: false }
      ],
      examHeaders: [
        { title: 'ئوقۇغۇچى', key: 'student_name', sortable: true },
        { title: 'نومۇر (0-100)', key: 'score', sortable: true },
        { title: 'ئىزاھات', key: 'notes', sortable: false }
      ],
      summaryHeaders: [
        { title: 'ئوقۇغۇچى', key: 'student_name', sortable: true },
        { title: 'سىنىپ', key: 'class_name', sortable: true },
        { title: 'يوقلىما (10)', key: 'attendance_score', sortable: true },
        { title: 'تەتىل تاپشۇرۇق (20)', key: 'vacation_score', sortable: true },
        { title: 'تاپشۇرۇق (30.6)', key: 'homework_score', sortable: true },
        { title: 'ئىمتىھان (39.4)', key: 'exam_score', sortable: true },
        { title: 'جەمئىي (100)', key: 'total_score', sortable: true }
      ],
      wheelStudents: [],
      pickedStudents: [],
      selectedStudent: null,
      isSwitching: false,
      currentDisplayName: 'سىنىپ تاللاڭ',
      switchingInterval: null,
      wheelRotation: 0,
      wheelSpinDurationMs: 5000,
      wheelInstantDurationMs: 1000,
      wheelInstant: false,
      wheelSnapRotation: false,
      wheelColors: [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFD93D',
        '#6C5CE7', '#A8E6CF', '#FF8A5B', '#74B9FF',
        '#FD79A8', '#55EFC4', '#FDCB6E', '#A29BFE',
        '#E17055', '#00CEC9', '#FAB1A0', '#81ECEC'
      ],
      wheelAttendanceRecords: [],
      
      // Teacher role and permissions
      teacherRole: 'teacher',
      canEdit: true,
      canFinalizeYear: false,
      currentSchoolYear: 2026,
      finalizingYear: false,
      homeworkVisibleFields: {
        new_class_preparation: true,
        main_text_reading: true,
        reading: true,
        exercise: true,
        spelling: true
      },
      homeworkPointValues: {
        new_class_preparation: 0.1,
        main_text_reading: 0.2,
        reading: 0.1,
        exercise: 0.4,
        spelling: 0.1
      },
      homeworkFieldLabels: {
        new_class_preparation: 'يېڭى دەرس تەييارلىقى',
        main_text_reading: 'تېكىست ئوقۇش',
        reading: 'ئوقۇشلۇق',
        exercise: 'كۆنۈكمە',
        spelling: 'ئىملا توغرىلاش'
      },
      
      // Class summary data (for grade_manager and manager)
      classAttendanceSummary: [],
      classHomeworkSummary: [],
      expandedAttendanceClasses: new Set(),
      expandedHomeworkClasses: new Set(),
      attendanceTabLoading: false,
      homeworkTabLoading: false,
      
      // Data
      attendanceRecords: [],
      students: [],
      teachers: [],
      
      // Summary data
      attendanceSummaryRecords: [],
      attendanceSummaryStudents: [],
      homeworkSummaryRecords: [],
      homeworkSummaryStudents: [],
      allAttendanceRecords: [],
      allHomeworkRecords: [],
      
      // Filters
      selectedClass: null,
      selectedDate: new Date().toISOString().split('T')[0],
      
      // Calendar highlighting
      attendanceDatesWithRecords: [],
      homeworkDatesWithRecords: [],
      attendanceDateMenu: false,
      homeworkDateMenu: false,
      
      // Statistics
      stats: [
        { title: 'جەمئىي ئوقۇغۇچى', value: 0, icon: 'mdi-account-group', color: 'blue' },
        { title: 'بار', value: 0, icon: 'mdi-check-circle', color: 'green' },
        { title: 'يوق', value: 0, icon: 'mdi-close-circle', color: 'red' },
        { title: 'كېچىككەن', value: 0, icon: 'mdi-clock-alert', color: 'orange' }
      ],
      
      // Student statistics
      studentStats: [
        { title: 'جەمئىي ئوقۇغۇچى', value: 0, icon: 'mdi-account-group', color: 'blue' },
        { title: 'ئاكتىپ ئوقۇغۇچى', value: 0, icon: 'mdi-account-check', color: 'green' },
        { title: 'سىنىپسىز', value: 0, icon: 'mdi-account-question', color: 'orange' },
        { title: 'يېڭى قوشۇلغان', value: 0, icon: 'mdi-account-plus', color: 'purple' }
      ],
      
      // Forms
      recordForm: {
        classId: null,
        date: new Date().toISOString().split('T')[0]
      },
      editForm: {
        id: null,
        status: '',
        notes: ''
      },
      
      // Student attendance tracking
      studentAttendance: {},
      studentNotes: {},
      
      // Student forms
      studentForm: {
        isim: '',
        famile: '',
        phone1: '',
        phone2: '',
        turushluq_doliti: '',
        tughulghan_waqti: '',
        sort_order: 0
      },
      editStudentForm: {
        id: null,
        isim: '',
        famile: '',
        phone1: '',
        phone2: '',
        turushluq_doliti: '',
        tughulghan_waqti: '',
        sort_order: 0,
        sinip: null
      },
      
      // Status options
      statusOptions: [
        { value: 'present', text: 'بار' },
        { value: 'absent', text: 'يوق' },
        { value: 'late', text: 'كېچىككەن' },
        { value: 'excused', text: 'روخسەت سورىغان' }
      ],
      
      // Table headers (base headers, actions added in computed)
      baseHeaders: [
        { title: 'ئوقۇغۇچى', key: 'student_name', sortable: true },
        { title: 'سىنىپ', key: 'class_name', sortable: true },
        { title: 'كۈن', key: 'date', sortable: true },
        { title: 'ھالەت', key: 'status', sortable: true },
        { title: 'ئىزاھات', key: 'notes', sortable: false },
        { title: 'خاتىرىلەنگەن', key: 'recorded_by_name', sortable: true }
      ],
      
      // Student table headers
      studentHeaders: [
        { title: 'تەرتىپ', key: 'row_number', sortable: false },
        { title: 'ئوقۇغۇچى', key: 'full_name', sortable: true },
        { title: 'سىنىپ', key: 'class_name', sortable: true },
        { title: 'تېلېفون 1', key: 'phone1', sortable: false },
        { title: 'تېلېفون 2', key: 'phone2', sortable: false },
        { title: 'تۇرۇشلۇق دۆلەت', key: 'turushluq_doliti', sortable: true },
        { title: 'ھەرىكەتلەر', key: 'actions', sortable: false }
      ],
      
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },

      // Homework management
      homeworkLoading: false,
      homeworkFormValid: false,
      editHomeworkFormValid: false,
      showRecordHomeworkDialog: false,
      showEditHomeworkDialog: false,
      selectedClassForHomework: null,
      selectedHomeworkDate: new Date().toISOString().split('T')[0],
      homeworkRecords: [],
      
      // Homework statistics
      homeworkStats: [
        { title: 'جەمئىي تاپشۇرۇق', value: 0, icon: 'mdi-book-education', color: 'blue' },
        { title: 'تاماملاندى', value: 0, icon: 'mdi-check-circle', color: 'green' },
        { title: 'تاماملانمىدى', value: 0, icon: 'mdi-close-circle', color: 'red' },
        { title: 'ئوتتۇرىچە', value: 0, icon: 'mdi-chart-line', color: 'orange' }
      ],
      
      // Homework forms
      homeworkForm: {
        classId: null,
        date: new Date().toISOString().split('T')[0],
        students: {}
      },
      editHomeworkForm: {
        id: null,
        notes: '',
        new_class_preparation: false,
        main_text_reading: false,
        reading: false,
        exercise: false,
        spelling: false,
        total_points: 0.0
      },
      
      // Homework table headers (base headers, actions added in computed)
      baseHomeworkHeaders: [
        { title: 'ئوقۇغۇچى', key: 'student_name', sortable: true },
        { title: 'سىنىپ', key: 'class_name', sortable: true },
        { title: 'كۈن', key: 'date', sortable: true },
        { title: 'يېڭى دەرس تەييارلىقى', key: 'new_class_preparation', sortable: true, visibleKey: 'new_class_preparation' },
        { title: 'تېكىست ئوقۇش', key: 'main_text_reading', sortable: true, visibleKey: 'main_text_reading' },
        { title: 'ئوقۇشلۇق', key: 'reading', sortable: true, visibleKey: 'reading' },
        { title: 'كۆنۈكمە', key: 'exercise', sortable: true, visibleKey: 'exercise' },
        { title: 'ئىملا توغرىلاش', key: 'spelling', sortable: true, visibleKey: 'spelling' },
        { title: 'جەمئىي نومۇر', key: 'total_points', sortable: true },
        { title: 'ئىزاھات', key: 'notes', sortable: false }
      ],
      
      // Summary table headers
      attendanceSummaryHeaders: [
        { title: 'كۈن', key: 'date', sortable: true },
        { title: 'بار', key: 'present_count', sortable: true },
        { title: 'كېچىككەن', key: 'late_count', sortable: true },
        { title: 'روخسەت سورىغان', key: 'excused_count', sortable: true },
        { title: 'يوق', key: 'absent_count', sortable: true }
      ],
      
      homeworkSummaryHeaders: [
        { title: 'كۈن', key: 'date', sortable: true },
        { title: 'تاپشۇرغانلار', key: 'homework_count', sortable: true },
        { title: 'تاپشۇرمىغانلار', key: 'no_homework_count', sortable: true }
      ]
    }
  },
  
  computed: {
    filteredStudents() {
      if (!this.selectedClassForStudents) {
        return this.students
      }
      return this.students.filter(student => student.sinip === this.selectedClassForStudents)
    },
    
    filteredClassesForStudents() {
      // For managers, only show classes if grade is selected
      if (!this.canEdit && (this.teacherRole === 'grade_manager' || this.teacherRole === 'manager')) {
        if (!this.selectedGradeForStudents) {
          return []
        }
        return this.teacherClasses.filter(cls => cls.grade_id === this.selectedGradeForStudents)
      }
      // For teachers, show all classes
      return this.teacherClasses
    },
    
    filteredClassesForWheel() {
      // For managers, only show classes if grade is selected
      if (!this.canEdit && (this.teacherRole === 'grade_manager' || this.teacherRole === 'manager')) {
        if (!this.selectedGradeForWheel) {
          return []
        }
        return this.teacherClasses.filter(cls => cls.grade_id === this.selectedGradeForWheel)
      }
      // For teachers, show all classes
      return this.teacherClasses
    },

    activeWheelSpinDurationMs() {
      return this.wheelInstant ? this.wheelInstantDurationMs : this.wheelSpinDurationMs
    },

    wheelSegments() {
      const count = this.wheelStudents.length
      if (count === 0) return []

      const slice = 360 / count
      const cx = 50
      const cy = 50
      const radius = 50

      return this.wheelStudents.map((student, index) => {
        const startAngle = index * slice
        const endAngle = (index + 1) * slice
        const midAngle = startAngle + slice / 2
        const labelRadius = count > 12 ? 34 : 38
        const labelPos = this.wheelPolarToCartesian(cx, cy, labelRadius, midAngle)
        const name = student.isim || ''
        const maxLen = count > 16 ? 6 : count > 10 ? 8 : 12
        // Radial labels: vertical under the top pointer, along each slice
        const radialAngle = midAngle - 90
        const labelAngle = midAngle > 90 && midAngle < 270
          ? radialAngle + 180
          : radialAngle

        return {
          id: student.id,
          path: this.wheelDescribeSlice(cx, cy, radius, startAngle, endAngle),
          color: this.wheelColors[index % this.wheelColors.length],
          label: name.length > maxLen ? `${name.slice(0, maxLen)}…` : name,
          labelX: labelPos.x,
          labelY: labelPos.y,
          labelAngle,
          fontSize: count > 18 ? 2.4 : count > 12 ? 2.8 : 3.4
        }
      })
    },

    filteredClassesForSummary() {
      if (!this.canEdit && (this.teacherRole === 'grade_manager' || this.teacherRole === 'manager')) {
        if (!this.selectedGradeForSummary) {
          return []
        }
        return this.teacherClasses.filter(cls => cls.grade_id === this.selectedGradeForSummary)
      }
      return this.teacherClasses
    },
    
    filteredAttendanceSummary() {
      // Flatten grade groups into a single array of classes
      let allClasses = []
      this.classAttendanceSummary.forEach(gradeGroup => {
        // Filter by selected grade if one is selected
        if (!this.selectedGradeForAttendanceSummary || gradeGroup.grade_id === this.selectedGradeForAttendanceSummary) {
          allClasses = allClasses.concat(gradeGroup.classes)
        }
      })
      return allClasses
    },
    
    filteredHomeworkSummary() {
      // Flatten grade groups into a single array of classes
      let allClasses = []
      this.classHomeworkSummary.forEach(gradeGroup => {
        // Filter by selected grade if one is selected
        if (!this.selectedGradeForHomeworkSummary || gradeGroup.grade_id === this.selectedGradeForHomeworkSummary) {
          allClasses = allClasses.concat(gradeGroup.classes)
        }
      })
      return allClasses
    },
    
    headers() {
      const headers = [...this.baseHeaders]
      if (this.canEdit) {
        headers.push({ title: 'مەشغۇلات', key: 'actions', sortable: false })
      }
      return headers
    },
    
    homeworkHeaders() {
      // Filter headers based on visible fields and use grade-aware labels
      const headers = this.baseHomeworkHeaders
        .filter(header => {
          if (header.visibleKey) {
            return this.homeworkVisibleFields[header.visibleKey] !== false
          }
          return true
        })
        .map(header => {
          if (header.visibleKey && this.homeworkFieldLabels[header.visibleKey]) {
            return { ...header, title: this.homeworkFieldLabels[header.visibleKey] }
          }
          return header
        })
      if (this.canEdit) {
        headers.push({ title: 'مەشغۇلات', key: 'actions', sortable: false })
      }
      return headers
    },
    
    groupedAttendanceSummary() {
      // Group students by class
      const classMap = new Map()
      
      this.attendanceSummaryStudents.forEach(student => {
        const classId = student.sinip || 'no-class'
        const className = student.class_name || 'سىنىپ يوق'
        const gradeName = student.grade_name || ''
        const gradeId = student.grade_id || null
        
        if (!classMap.has(classId)) {
          classMap.set(classId, {
            classId: classId,
            className: className,
            gradeName: gradeName,
            gradeId: gradeId,
            students: [],
            studentIds: []
          })
        }
        
        classMap.get(classId).students.push(student)
        classMap.get(classId).studentIds.push(student.id)
      })
      
      // For each class, get unique dates from records for students in that class
      return Array.from(classMap.values())
        .sort((a, b) => {
          if (a.classId === 'no-class') return 1
          if (b.classId === 'no-class') return -1
          
          // First sort by grade name, then by class name
          if (a.gradeName && b.gradeName) {
            const gradeCompare = a.gradeName.localeCompare(b.gradeName)
            if (gradeCompare !== 0) return gradeCompare
          } else if (a.gradeName && !b.gradeName) return -1
          else if (!a.gradeName && b.gradeName) return 1
          
          return a.className.localeCompare(b.className)
        })
        .map(group => {
          // Get unique dates for this class's students
          const classDates = [...new Set(
            this.allAttendanceRecords
              .filter(record => group.studentIds.includes(record.student_id))
              .map(record => record.date)
          )].sort((a, b) => new Date(a) - new Date(b))
          
          return {
            ...group,
            dates: classDates.map(date => ({ date })),
            students: group.students.sort((a, b) => {
              if (a.sort_order !== b.sort_order) {
                return (a.sort_order || 0) - (b.sort_order || 0)
              }
              return a.id - b.id
            })
          }
        })
    },
    
    groupedHomeworkSummary() {
      // Group students by class
      const classMap = new Map()
      
      this.homeworkSummaryStudents.forEach(student => {
        const classId = student.sinip || 'no-class'
        const className = student.class_name || 'سىنىپ يوق'
        const gradeName = student.grade_name || ''
        const gradeId = student.grade_id || null
        
        if (!classMap.has(classId)) {
          classMap.set(classId, {
            classId: classId,
            className: className,
            gradeName: gradeName,
            gradeId: gradeId,
            students: [],
            studentIds: []
          })
        }
        
        classMap.get(classId).students.push(student)
        classMap.get(classId).studentIds.push(student.id)
      })
      
      // For each class, get unique dates from records for students in that class
      return Array.from(classMap.values())
        .sort((a, b) => {
          if (a.classId === 'no-class') return 1
          if (b.classId === 'no-class') return -1
          
          // First sort by grade name, then by class name
          if (a.gradeName && b.gradeName) {
            const gradeCompare = a.gradeName.localeCompare(b.gradeName)
            if (gradeCompare !== 0) return gradeCompare
          } else if (a.gradeName && !b.gradeName) return -1
          else if (!a.gradeName && b.gradeName) return 1
          
          return a.className.localeCompare(b.className)
        })
        .map(group => {
          // Get unique dates for this class's students
          const classDates = [...new Set(
            this.allHomeworkRecords
              .filter(record => group.studentIds.includes(record.student_id))
              .map(record => record.date)
          )].sort((a, b) => new Date(a) - new Date(b))
          
          return {
            ...group,
            dates: classDates.map(date => ({ date })),
            students: group.students.sort((a, b) => {
              if (a.sort_order !== b.sort_order) {
                return (a.sort_order || 0) - (b.sort_order || 0)
              }
              return a.id - b.id
            })
          }
        })
    }
  },

  watch: {
    'homeworkForm.classId'(newClassId) {
      if (newClassId) {
        this.initializeStudentHomeworkData(newClassId)
      }
    },
    
    async activeTab(newTab) {
      // Load data when switching to a tab
      // For managers, don't auto-load - wait for grade selection
      if (newTab === 'attendance' && this.canEdit) {
        // Load attendance data for teachers
        if (this.attendanceRecords.length === 0 && !this.loading) {
          await this.loadAttendanceData()
        } else {
          // Even if records exist, reload dates to ensure calendar is up to date
          await this.loadAttendanceDates()
        }
      } else if (newTab === 'homework' && this.canEdit) {
        // Load homework data for teachers
        if (this.homeworkRecords.length === 0 && !this.homeworkLoading) {
          await this.loadHomeworkData()
        } else {
          // Even if records exist, reload dates to ensure calendar is up to date
          await this.loadHomeworkDates()
        }
      } else if (newTab === 'vacation-homework' && this.canEdit) {
        if (this.selectedClassForVacation) {
          await this.loadVacationData()
        }
      } else if (newTab === 'exam' && this.canEdit) {
        if (this.selectedClassForExam) {
          await this.loadExamData()
        }
      } else if (newTab === 'summary') {
        if (this.selectedClassForSummary) {
          await this.loadSummaryData()
        }
      }
      // Managers will load data when they select a grade
    }
  },
  
  async mounted() {
    // Load teacher profile first (needed for role check)
    await this.loadTeacherProfile()
    
    // Load classes for all roles (needed for selectors)
    await this.loadTeacherClasses()
    
    // Load initial tab data asynchronously (non-blocking)
    // This allows the tab to show immediately while data loads
    this.$nextTick(() => {
      if (this.teacherRole === 'grade_manager' || this.teacherRole === 'manager') {
        // For managers, don't auto-load - wait for grade selection
        // Data will load when grade is selected
      } else {
        // For teachers, load initial data
        this.loadInitialData().then(() => {
          if (this.activeTab === 'homework') {
            this.loadHomeworkData()
          }
        })
      }
    })
  },
  
  beforeUnmount() {
    // Clean up spin timers
    if (this.switchingInterval) {
      clearTimeout(this.switchingInterval)
      this.switchingInterval = null
    }
  },
  
  methods: {
    isAttendanceDateHighlighted(date) {
      // Check if date should be highlighted (has attendance records)
      // Uses date-only comparison (ignores time)
      return this.dateExistsInArray(date, this.attendanceDatesWithRecords || [])
    },
    
    isHomeworkDateHighlighted(date) {
      // Check if date should be highlighted (has homework records)
      // Uses date-only comparison (ignores time)
      return this.dateExistsInArray(date, this.homeworkDatesWithRecords || [])
    },
    
    normalizeDate(date) {
      // Normalize date to YYYY-MM-DD format (date-only, no time)
      if (!date) return null
      
      let dateObj
      
      if (typeof date === 'string') {
        // If already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
          return date
        }
        // Parse string date
        dateObj = new Date(date)
      } else if (date instanceof Date) {
        dateObj = date
      } else {
        return null
      }
      
      // Check for invalid date
      if (isNaN(dateObj.getTime())) {
        return null
      }
      
      // Return in YYYY-MM-DD format (date-only, no time)
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const day = String(dateObj.getDate()).padStart(2, '0')
      
      return `${year}-${month}-${day}`
    },
    
    dateExistsInArray(targetDate, dateArray) {
      // Check if a date exists in an array of dates (date-only comparison)
      if (!targetDate || !Array.isArray(dateArray)) {
        return false
      }
      
      const normalizedTarget = this.normalizeDate(targetDate)
      if (!normalizedTarget) return false
      
      return dateArray.some(date => {
        const normalized = this.normalizeDate(date)
        return normalized === normalizedTarget
      })
    },
    
    async loadTeacherProfile() {
      try {
        const response = await attendanceApi.getTeacherProfile()
        this.teacherRole = response.data.role || 'teacher'
        // canEdit should be false for grade_manager and manager roles
        this.canEdit = response.data.can_edit !== false && this.teacherRole === 'teacher'
        this.canFinalizeYear = response.data.can_finalize_year === true || this.teacherRole === 'manager'
        if (response.data.current_school_year) {
          this.currentSchoolYear = response.data.current_school_year
        }
        
        // Store homework visibility and point values
        if (response.data.homework_visible_fields) {
          this.homeworkVisibleFields = response.data.homework_visible_fields
        }
        if (response.data.homework_point_values) {
          this.homeworkPointValues = response.data.homework_point_values
        }
        if (response.data.homework_field_labels) {
          this.homeworkFieldLabels = response.data.homework_field_labels
        }
        
        // Re-extract grades if classes are already loaded (in case role changed)
        if (this.teacherClasses.length > 0) {
          this.extractGradesFromClasses()
        }
      } catch (error) {
        console.error('Error loading teacher profile:', error)
        // Default to teacher role if API fails
        this.teacherRole = 'teacher'
        this.canEdit = true
        this.canFinalizeYear = false
      }
    },
    
    async loadAttendanceSummary() {
      // Only load if a grade is selected
      if (!this.selectedGradeForAttendanceSummary) {
        this.classAttendanceSummary = []
        return
      }
      
      try {
        this.attendanceTabLoading = true
        const attendanceResponse = await attendanceApi.getClassAttendanceSummary()
        this.classAttendanceSummary = attendanceResponse.data || []
        // Also load students and attendance records for detail views
        if (this.students.length === 0) {
          await this.loadStudents()
        }
        if (this.allAttendanceRecords.length === 0) {
          await this.loadAllAttendanceRecords()
        }
      } catch (error) {
        console.error('Error loading attendance summary:', error)
        this.showSnackbar('يوقلىما خاتىرىسىنى يۈكلەشتە خاتالىق', 'error')
      } finally {
        this.attendanceTabLoading = false
      }
    },
    
    async loadHomeworkSummary() {
      // Only load if a grade is selected
      if (!this.selectedGradeForHomeworkSummary) {
        this.classHomeworkSummary = []
        return
      }
      
      try {
        this.homeworkTabLoading = true
        const homeworkResponse = await attendanceApi.getClassHomeworkSummary()
        this.classHomeworkSummary = homeworkResponse.data || []
        // Also load students and homework records for detail views
        if (this.students.length === 0) {
          await this.loadStudents()
        }
        if (this.allHomeworkRecords.length === 0) {
          await this.loadAllHomeworkRecords()
        }
      } catch (error) {
        console.error('Error loading homework summary:', error)
        this.showSnackbar('تاپشۇرۇق خاتىرىسىنى يۈكلەشتە خاتالىق', 'error')
      } finally {
        this.homeworkTabLoading = false
      }
    },
    
    async loadClassSummaries() {
      // Legacy method - kept for compatibility, but now uses separate methods
      await Promise.all([
        this.loadAttendanceSummary(),
        this.loadHomeworkSummary()
      ])
    },
    
    async loadAllAttendanceRecords() {
      try {
        const response = await attendanceApi.getTeacherAttendance()
        this.allAttendanceRecords = response.data || []
      } catch (error) {
        console.error('Error loading all attendance records:', error)
      }
    },
    
    async loadAllHomeworkRecords() {
      try {
        const response = await attendanceApi.getTeacherHomework()
        this.allHomeworkRecords = response.data || []
      } catch (error) {
        console.error('Error loading all homework records:', error)
      }
    },
    
    toggleAttendanceDetails(classId) {
      if (this.expandedAttendanceClasses.has(classId)) {
        this.expandedAttendanceClasses.delete(classId)
      } else {
        this.expandedAttendanceClasses.add(classId)
      }
    },
    
    toggleHomeworkDetails(classId) {
      if (this.expandedHomeworkClasses.has(classId)) {
        this.expandedHomeworkClasses.delete(classId)
      } else {
        this.expandedHomeworkClasses.add(classId)
      }
    },
    
    isAttendanceExpanded(classId) {
      return this.expandedAttendanceClasses.has(classId)
    },
    
    isHomeworkExpanded(classId) {
      return this.expandedHomeworkClasses.has(classId)
    },
    
    getClassAttendanceRecords(classId) {
      return this.allAttendanceRecords.filter(record => {
        const student = this.students.find(s => s.id === record.student_id)
        return student && student.sinip === classId
      })
    },
    
    getClassHomeworkRecords(classId) {
      return this.allHomeworkRecords.filter(record => {
        const student = this.students.find(s => s.id === record.student_id)
        return student && student.sinip === classId
      })
    },
    
    async loadInitialData() {
      try {
        this.loading = true
        await Promise.all([
          this.loadStudents(),
          this.loadTeacherClasses(),
          this.loadAttendanceData()
        ])
        // Load dates for calendar highlighting
        await Promise.all([
          this.loadAttendanceDates(),
          this.loadHomeworkDates()
        ])
      } catch (error) {
        this.showSnackbar('مەلۇمات يۈكلەش خاتالىقى', 'error')
        console.error('Error loading initial data:', error)
      } finally {
        this.loading = false
      }
    },
    
    async loadStudents() {
      try {
        const response = await attendanceApi.getTeacherStudents()
        this.students = response.data
        this.updateStudentStatistics()
        // Initialize student attendance tracking
        this.students.forEach(student => {
          this.studentAttendance[student.id] = 'present'
          this.studentNotes[student.id] = ''
        })
      } catch (error) {
        console.error('Error loading students:', error)
      }
    },
    
    async loadStudentsForClass() {
      // Load students only for the selected class
      if (!this.selectedClassForStudents) {
        this.students = []
        return
      }
      
      try {
        this.loading = true
        const response = await attendanceApi.getTeacherStudents(this.selectedClassForStudents)
        this.students = response.data || []
        this.updateStudentStatistics()
      } catch (error) {
        console.error('Error loading students for class:', error)
        this.showSnackbar('ئوقۇغۇچىلارنى يۈكلەشتە خاتالىق', 'error')
      } finally {
        this.loading = false
      }
    },
    
    async loadTeacherClasses() {
      try {
        const response = await attendanceApi.getTeacherClasses()
        this.teacherClasses = response.data || []
        
        // Always extract unique grades from classes (will be used if user is manager)
        this.extractGradesFromClasses()
      } catch (error) {
        console.error('Error loading teacher classes:', error)
        console.error('Error response:', error.response?.data)
      }
    },
    
    extractGradesFromClasses() {
      // Extract unique grades from classes
      const gradesMap = new Map()
      this.teacherClasses.forEach(cls => {
        if (cls.grade_id && cls.grade_name) {
          if (!gradesMap.has(cls.grade_id)) {
            gradesMap.set(cls.grade_id, {
              id: cls.grade_id,
              isim: cls.grade_name,
              no: cls.grade_no || 0  // Use grade_no if available, default to 0
            })
          }
        }
      })
      this.availableGrades = Array.from(gradesMap.values()).sort((a, b) => {
        // Sort by no field first, then by name
        if (a.no !== b.no) {
          return (a.no || 0) - (b.no || 0)
        }
        return a.isim.localeCompare(b.isim)
      })
    },
    
    onGradeSelectedForStudents() {
      // Clear class selection when grade changes
      this.selectedClassForStudents = null
      this.students = []
    },
    
    onGradeSelectedForWheel() {
      // Clear class selection when grade changes
      this.selectedClassForWheel = null
      this.wheelStudents = []
      this.updateCircleDisplay()
    },
    
    async onGradeSelectedForAttendanceSummary() {
      // Load attendance summary when grade is selected
      if (this.selectedGradeForAttendanceSummary) {
        // Only load if not already loaded or if we need to reload
        if (this.classAttendanceSummary.length === 0 || !this.attendanceTabLoading) {
          await this.loadAttendanceSummary()
        }
      } else {
        // Clear summary when grade is cleared
        this.classAttendanceSummary = []
      }
    },
    
    async onGradeSelectedForHomeworkSummary() {
      // Load homework summary when grade is selected
      if (this.selectedGradeForHomeworkSummary) {
        // Only load if not already loaded or if we need to reload
        if (this.classHomeworkSummary.length === 0 || !this.homeworkTabLoading) {
          await this.loadHomeworkSummary()
        }
      } else {
        // Clear summary when grade is cleared
        this.classHomeworkSummary = []
      }
    },
    
    updateStudentStatistics() {
      const totalStudents = this.students.length
      const studentsWithClass = this.students.filter(student => student.sinip).length
      const studentsWithoutClass = totalStudents - studentsWithClass
      const newStudents = this.students.filter(student => {
        const createdDate = new Date(student.created_at || student.id)
        const now = new Date()
        const diffTime = Math.abs(now - createdDate)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        return diffDays <= 7 // New students in last 7 days
      }).length
      
      this.studentStats[0].value = totalStudents
      this.studentStats[1].value = studentsWithClass
      this.studentStats[2].value = studentsWithoutClass
      this.studentStats[3].value = newStudents
    },
    
    async loadAttendanceDates() {
      try {
        const classId = this.selectedClass || null
        const response = await attendanceApi.getTeacherAttendanceDates(classId)
        const dates = response.data.dates || []
        // Normalize dates to YYYY-MM-DD format for v-date-picker
        this.attendanceDatesWithRecords = dates.map(date => {
          if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date
          }
          // If it's an ISO string or Date object, extract YYYY-MM-DD
          const dateObj = date instanceof Date ? date : new Date(date)
          if (isNaN(dateObj.getTime())) {
            return date
          }
          const year = dateObj.getFullYear()
          const month = String(dateObj.getMonth() + 1).padStart(2, '0')
          const day = String(dateObj.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        })
        console.log('Attendance dates with records (raw):', this.attendanceDatesWithRecords)
        console.log('Attendance dates array type:', Array.isArray(this.attendanceDatesWithRecords))
        console.log('First date:', this.attendanceDatesWithRecords[0], typeof this.attendanceDatesWithRecords[0])
        // Ensure all dates are normalized to YYYY-MM-DD format and convert to plain array
        const normalizedDates = this.attendanceDatesWithRecords.map(d => this.normalizeDate(d)).filter(d => d)
        this.attendanceDatesWithRecords = [...normalizedDates] // Convert to plain array
        console.log('Attendance dates normalized (plain array):', this.attendanceDatesWithRecords)
      } catch (error) {
        console.error('Error loading attendance dates:', error)
        this.attendanceDatesWithRecords = []
      }
    },
    
    async onClassSelectedForAttendance() {
      await this.loadAttendanceData()
      await this.loadAttendanceDates()
    },
    
    async onDateSelected(date) {
      this.attendanceDateMenu = false
      // Format date to YYYY-MM-DD if it's a Date object or ISO string
      this.selectedDate = this.formatDateForAPI(date)
      await this.loadAttendanceData()
    },
    
    async onHomeworkDateSelected(date) {
      this.homeworkDateMenu = false
      // Format date to YYYY-MM-DD if it's a Date object or ISO string
      this.selectedHomeworkDate = this.formatDateForAPI(date)
      await this.loadHomeworkData()
    },
    
    formatDateForAPI(date) {
      // Handle null/undefined
      if (!date) return null
      
      // If already in YYYY-MM-DD format, return as is
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date
      }
      
      // If it's a Date object or ISO string, extract YYYY-MM-DD
      const dateObj = date instanceof Date ? date : new Date(date)
      if (isNaN(dateObj.getTime())) {
        // Invalid date, return original or null
        return typeof date === 'string' ? date : null
      }
      
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const day = String(dateObj.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    async loadAttendanceData() {
      try {
        this.loading = true
        const params = {}
        
        if (this.selectedClass) {
          params.class_id = this.selectedClass
        }
        if (this.selectedDate) {
          // Ensure date is in YYYY-MM-DD format
          const formattedDate = this.formatDateForAPI(this.selectedDate)
          params.start_date = formattedDate
          params.end_date = formattedDate
        }
        
        const response = await attendanceApi.getTeacherAttendanceStatistics(params)
        this.updateStatistics(response.data)
        
        // Load detailed records
        if (this.selectedDate) {
          const formattedDate = this.formatDateForAPI(this.selectedDate)
          const recordsResponse = await attendanceApi.getTeacherAttendanceByDate(formattedDate)
          this.attendanceRecords = recordsResponse.data
        } else {
          const recordsResponse = await attendanceApi.getTeacherAttendance()
          this.attendanceRecords = recordsResponse.data
        }
        
        // Always load all records for summary table
        const allRecordsResponse = await attendanceApi.getTeacherAttendance()
        this.allAttendanceRecords = allRecordsResponse.data
        
        this.updateAttendanceSummary()
        
        // Load dates with records for calendar highlighting
        await this.loadAttendanceDates()
      } catch (error) {
        this.showSnackbar('يوقلىما مەلۇماتى يۈكلەش خاتالىقى', 'error')
        console.error('Error loading attendance data:', error)
      } finally {
        this.loading = false
      }
    },
    
    updateStatistics(data) {
      const totalStudents = data.length
      const presentCount = data.filter(item => item.present_days > 0).length
      const absentCount = data.filter(item => item.absent_days > 0).length
      const lateCount = data.filter(item => item.late_days > 0).length
      
      this.stats[0].value = totalStudents
      this.stats[1].value = presentCount
      this.stats[2].value = absentCount
      this.stats[3].value = lateCount
    },
    
    async saveAttendance() {
      try {
        if (!this.$refs.form.validate()) return
        
        const attendanceRecords = this.students
          .filter(student => student.sinip === this.recordForm.classId)
          .map(student => ({
            student_id: student.id,
            status: this.studentAttendance[student.id] || 'present',
            notes: this.studentNotes[student.id] || ''
          }))
        
        const data = {
          date: this.recordForm.date,
          attendance_records: attendanceRecords
        }
        
        await attendanceApi.bulkRecordAttendance(data)
        this.showSnackbar('يوقلىما ئەھۋالى مۇۋەپپەقىيەتلىك ساقلاندى', 'success')
        this.closeRecordDialog()
        await this.loadAttendanceData()
      } catch (error) {
        this.showSnackbar('يوقلىما ئەھۋالى ساقلاش خاتالىقى', 'error')
        console.error('Error saving attendance:', error)
      }
    },
    
    editAttendance(item) {
      this.editForm = {
        id: item.id,
        status: item.status,
        notes: item.notes || ''
      }
      this.showEditDialog = true
    },
    
    async updateAttendance() {
      try {
        if (!this.$refs.editForm.validate()) return
        
        await attendanceApi.updateAttendance(this.editForm.id, {
          status: this.editForm.status,
          notes: this.editForm.notes
        })
        
        this.showSnackbar('يوقلىما ئەھۋالى مۇۋەپپەقىيەتلىك يېڭىلاندى', 'success')
        this.showEditDialog = false
        await this.loadAttendanceData()
      } catch (error) {
        this.showSnackbar('يوقلىما يېڭىلاش خاتالىقى', 'error')
        console.error('Error updating attendance:', error)
      }
    },
    
    async deleteAttendance(id) {
      if (confirm('بۇ يوقلىما خاتىرىسىنى ئۆچۈرەمسىز؟')) {
        try {
          await attendanceApi.deleteAttendance(id)
          this.showSnackbar('يوقلىما خاتىرىسى ئۆچۈرۈلدى', 'success')
          await this.loadAttendanceData()
        } catch (error) {
          this.showSnackbar('يوقلىما ئۆچۈرۈش خاتالىقى', 'error')
          console.error('Error deleting attendance:', error)
        }
      }
    },
    
    getStatusColor(status) {
      const colors = {
        present: 'green',
        absent: 'red',
        late: 'orange',
        excused: 'blue'
      }
      return colors[status] || 'grey'
    },
    
    getStatusText(status) {
      const texts = {
        present: 'بار',
        absent: 'يوق',
        late: 'كېچىككەن',
        excused: 'رۇخسەت'
      }
      return texts[status] || status
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      // If already in YYYY-MM-DD format, return as is
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString
      }
      // Otherwise format to YYYY-MM-DD
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    },
    
    // Student management methods
    async saveStudent() {
      try {
        if (!this.$refs.studentForm.validate()) return
        
        // Use the selected class for the new student
        const studentData = {
          ...this.studentForm,
          sinip: this.selectedClassForStudents
        }
        
        // Set default sort_order if not provided
        if (!studentData.sort_order && studentData.sort_order !== 0) {
          // Get the highest sort_order for this class and add 1
          const classStudents = this.students.filter(s => s.sinip === this.selectedClassForStudents)
          const maxSortOrder = Math.max(...classStudents.map(s => s.sort_order || 0), 0)
          studentData.sort_order = maxSortOrder + 1
        }
        
        // Handle empty date field - convert empty string to null
        if (!studentData.tughulghan_waqti) {
          studentData.tughulghan_waqti = null
        }
        
        // Handle empty phone fields - convert empty strings to null
        if (!studentData.phone1) {
          studentData.phone1 = null
        }
        if (!studentData.phone2) {
          studentData.phone2 = null
        }
        
        await attendanceApi.createStudent(studentData)
        this.showSnackbar('ئوقۇغۇچى مۇۋەپپەقىيەتلىك قوشۇلدى', 'success')
        this.showAddStudentDialog = false
        this.resetStudentForm()
        await this.loadStudentsForClass()
      } catch (error) {
        this.showSnackbar('ئوقۇغۇچى قوشۇش خاتالىقى', 'error')
        console.error('Error saving student:', error)
        console.error('Error response:', error.response?.data)
        console.error('Error status:', error.response?.status)
        console.error('Error headers:', error.response?.headers)
      }
    },
    
    editStudent(student) {
      this.editStudentForm = {
        id: student.id,
        isim: student.isim,
        famile: student.famile,
        phone1: student.phone1 || '',
        phone2: student.phone2 || '',
        turushluq_doliti: student.turushluq_doliti,
        tughulghan_waqti: student.tughulghan_waqti || '',
        sort_order: student.sort_order || 0,
        sinip: student.sinip
      }
      this.showEditStudentDialog = true
    },
    
    async updateStudent() {
      try {
        if (!this.$refs.editStudentForm.validate()) return
        
        const updateData = { ...this.editStudentForm }
        
        // Handle empty date field - convert empty string to null
        if (!updateData.tughulghan_waqti) {
          updateData.tughulghan_waqti = null
        }
        
        // Handle empty phone fields - convert empty strings to null
        if (!updateData.phone1) {
          updateData.phone1 = null
        }
        if (!updateData.phone2) {
          updateData.phone2 = null
        }
        
        await attendanceApi.updateStudent(updateData.id, updateData)
        this.showSnackbar('ئوقۇغۇچى مۇۋەپپەقىيەتلىك يېڭىلاندى', 'success')
        this.showEditStudentDialog = false
        await this.loadStudentsForClass()
      } catch (error) {
        this.showSnackbar('ئوقۇغۇچى يېڭىلاش خاتالىقى', 'error')
        console.error('Error updating student:', error)
      }
    },
    
    async deleteStudent(id) {
      if (confirm('بۇ ئوقۇغۇچىنى ئۆچۈرەمسىز؟')) {
        try {
          await attendanceApi.deleteStudent(id)
          this.showSnackbar('ئوقۇغۇچى ئۆچۈرۈلدى', 'success')
          await this.loadStudentsForClass()
        } catch (error) {
          this.showSnackbar('ئوقۇغۇچى ئۆچۈرۈش خاتالىقى', 'error')
          console.error('Error deleting student:', error)
        }
      }
    },
    
    resetStudentForm() {
      this.studentForm = {
        isim: '',
        famile: '',
        phone1: '',
        phone2: '',
        turushluq_doliti: '',
        tughulghan_waqti: '',
        sort_order: 0
      }
    },
    
    getSelectedClassName() {
      if (!this.selectedClassForStudents) {
        return 'سىنىپ تاللانمىدى'
      }
      const selectedClass = this.teacherClasses.find(c => c.id === this.selectedClassForStudents)
      return selectedClass ? (selectedClass.display_name || selectedClass.isim) : 'نامەلۇم سىنىپ'
    },
    
    logout() {
      if (confirm('سىز چىقىشنى خالامسىز؟')) {
        this.$store.commit('logout')
        this.$router.push('/login')
      }
    },
    
    // Name picker functionality methods
    async loadWheelStudents() {
      if (!this.selectedClassForWheel) {
        this.wheelStudents = []
        this.updateCircleDisplay()
        return
      }
      
      try {
        // Get today's date in YYYY-MM-DD format
        const today = new Date().toISOString().split('T')[0]
        
        // Try to get attendance records for today
        let attendedStudents = []
        try {
          const attendanceResponse = await attendanceApi.getTeacherAttendanceByDate(today)
          const attendanceRecords = attendanceResponse.data
          
          // Find the class name for the selected class ID
          const selectedClass = this.teacherClasses.find(c => c.id === this.selectedClassForWheel)
          const selectedClassName = selectedClass ? (selectedClass.display_name || selectedClass.isim) : null
          
          // Store attendance records for display purposes
          this.wheelAttendanceRecords = attendanceRecords.filter(record => 
            record.class_name === selectedClassName
          )
          
          // Filter attendance records for the selected class and present status
          attendedStudents = attendanceRecords.filter(record => 
            record.class_name === selectedClassName && 
            record.status === 'present'
          ).map(record => ({
            id: record.student_id,
            isim: record.student_name,
            famile: record.student_family,
            sinip: this.selectedClassForWheel
          }))
        } catch (error) {
          this.wheelAttendanceRecords = []
        }
        
        // If we have attended students for today, use them; otherwise use all students
        let availableStudents = []
        if (attendedStudents.length > 0) {
          availableStudents = attendedStudents
        } else {
          // Fall back to all students in the selected class
          availableStudents = this.students.filter(student => 
            student.sinip === this.selectedClassForWheel
          )
        }
        
        // Exclude already picked students (keep current winner on the wheel)
        const pickedStudentIds = this.pickedStudents.map(s => s.id)
        const winnerId = this.selectedStudent?.id
        this.wheelStudents = availableStudents.filter(student =>
          !pickedStudentIds.includes(student.id) || student.id === winnerId
        )
        
        this.updateCircleDisplay()
      } catch (error) {
        console.error('Error loading wheel students:', error)
        // Fall back to all students if there's an error
        const pickedStudentIds = this.pickedStudents.map(s => s.id)
        const winnerId = this.selectedStudent?.id
        this.wheelStudents = this.students.filter(student =>
          student.sinip === this.selectedClassForWheel &&
          (!pickedStudentIds.includes(student.id) || student.id === winnerId)
        )
        this.updateCircleDisplay()
      }
    },
    
    wheelPolarToCartesian(cx, cy, radius, angleInDegrees) {
      const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180
      return {
        x: cx + radius * Math.cos(angleInRadians),
        y: cy + radius * Math.sin(angleInRadians)
      }
    },

    wheelDescribeSlice(cx, cy, radius, startAngle, endAngle) {
      // Full circle edge case (single student)
      if (endAngle - startAngle >= 359.99) {
        return `M ${cx - radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx + radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx - radius} ${cy} Z`
      }

      const start = this.wheelPolarToCartesian(cx, cy, radius, startAngle)
      const end = this.wheelPolarToCartesian(cx, cy, radius, endAngle)
      const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0

      return [
        `M ${cx} ${cy}`,
        `L ${start.x} ${start.y}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
        'Z'
      ].join(' ')
    },

    async pickStudent() {
      if (this.isSwitching || this.wheelStudents.length === 0) return

      if (this.switchingInterval) {
        clearTimeout(this.switchingInterval)
        this.switchingInterval = null
      }

      // Remove previous winner only when starting the next spin, so the
      // pointer keeps showing the correct name after the wheel stops.
      if (this.selectedStudent) {
        const prevId = this.selectedStudent.id
        this.wheelSnapRotation = true
        this.wheelStudents = this.wheelStudents.filter(s => s.id !== prevId)
        this.wheelRotation = ((this.wheelRotation % 360) + 360) % 360
        this.selectedStudent = null
        await this.$nextTick()
        // Force reflow so the next spin transition is not skipped
        void this.$el.offsetWidth
        this.wheelSnapRotation = false
      }

      if (this.wheelStudents.length === 0) {
        this.updateCircleDisplay()
        return
      }

      this.isSwitching = true

      const spinDurationMs = this.activeWheelSpinDurationMs
      const count = this.wheelStudents.length
      const winnerIndex = Math.floor(Math.random() * count)
      const slice = 360 / count
      const segmentCenter = winnerIndex * slice + slice / 2
      // Pointer is at the top. After clockwise rotation R, angle under pointer is (360 - R%360) % 360.
      const targetMod = (360 - segmentCenter + 360) % 360
      const extraSpins = this.wheelInstant
        ? 1 + Math.floor(Math.random() * 2)
        : 5 + Math.floor(Math.random() * 3)
      const currentMod = ((this.wheelRotation % 360) + 360) % 360
      const deltaToTarget = (targetMod - currentMod + 360) % 360
      this.wheelRotation = this.wheelRotation + extraSpins * 360 + deltaToTarget

      this.switchingInterval = setTimeout(() => {
        this.finalizeWheelPick(winnerIndex)
        this.isSwitching = false
        this.switchingInterval = null
      }, spinDurationMs)
    },

    finalizeWheelPick(winnerIndex) {
      if (this.wheelStudents.length === 0) return

      const selectedStudent = this.wheelStudents[winnerIndex]
      if (!selectedStudent) return

      this.selectedStudent = selectedStudent
      if (!this.pickedStudents.some(s => s.id === selectedStudent.id)) {
        this.pickedStudents.push(selectedStudent)
      }
      // Keep winner on the wheel until the next spin so the pointer stays correct.
      this.updateCircleDisplay()
      this.showSnackbar(`${selectedStudent.isim} ${selectedStudent.famile} تاللاندى!`, 'success')
    },
    
    removePickedStudent(studentId) {
      const studentIndex = this.pickedStudents.findIndex(s => s.id === studentId)
      if (studentIndex === -1) return
      
      const removedStudent = this.pickedStudents[studentIndex]
      this.pickedStudents.splice(studentIndex, 1)

      if (this.selectedStudent && this.selectedStudent.id === studentId) {
        this.selectedStudent = null
      }
      
      // Add back to wheel students if missing and class is still selected
      if (
        this.selectedClassForWheel &&
        removedStudent.sinip === this.selectedClassForWheel &&
        !this.wheelStudents.some(s => s.id === removedStudent.id)
      ) {
        this.wheelStudents.push(removedStudent)
      }
      
      // Update circle display
      this.updateCircleDisplay()
      
      this.showSnackbar(`${removedStudent.isim} ${removedStudent.famile} تىزىملىكتىن ئۆچۈرۈلدى`, 'info')
    },
    
    clearPickedStudents() {
      if (confirm('بارلىق تاللانغان ئوقۇغۇچىلارنى ئۆچۈرەمسىز؟')) {
        // Add picked students back to wheel if missing and class is selected
        if (this.selectedClassForWheel) {
          const existingIds = new Set(this.wheelStudents.map(s => s.id))
          const studentsToAddBack = this.pickedStudents.filter(
            s => s.sinip === this.selectedClassForWheel && !existingIds.has(s.id)
          )
          this.wheelStudents.push(...studentsToAddBack)
        }
        
        this.pickedStudents = []
        this.selectedStudent = null
        
        // Update circle display
        this.updateCircleDisplay()
        
        this.showSnackbar('بارلىق تاللانغان ئوقۇغۇچىلار ئۆچۈرۈلدى', 'info')
      }
    },
    
    updateCircleDisplay() {
      if (!this.selectedClassForWheel) {
        this.currentDisplayName = 'سىنىپ تاللاڭ'
        return
      }

      // Count students still available for the next spin (exclude current winner)
      const remaining = this.wheelStudents.filter(
        s => !this.selectedStudent || s.id !== this.selectedStudent.id
      ).length

      if (remaining === 0) {
        this.currentDisplayName = 'بارلىق ئوقۇغۇچىلار تاللاندى'
      } else {
        this.currentDisplayName = `${remaining} ئوقۇغۇچى قالدى`
      }
    },

    // Homework Management Methods
    async loadHomeworkDates() {
      try {
        const classId = this.selectedClassForHomework || null
        const response = await attendanceApi.getTeacherHomeworkDates(classId)
        const dates = response.data.dates || []
        // Normalize dates to YYYY-MM-DD format for v-date-picker
        this.homeworkDatesWithRecords = dates.map(date => {
          if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date
          }
          // If it's an ISO string or Date object, extract YYYY-MM-DD
          const dateObj = date instanceof Date ? date : new Date(date)
          if (isNaN(dateObj.getTime())) {
            return date
          }
          const year = dateObj.getFullYear()
          const month = String(dateObj.getMonth() + 1).padStart(2, '0')
          const day = String(dateObj.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        })
        console.log('Homework dates with records (raw):', this.homeworkDatesWithRecords)
        // Ensure all dates are normalized to YYYY-MM-DD format and convert to plain array
        const normalizedDates = this.homeworkDatesWithRecords.map(d => this.normalizeDate(d)).filter(d => d)
        this.homeworkDatesWithRecords = [...normalizedDates] // Convert to plain array
        console.log('Homework dates normalized (plain array):', this.homeworkDatesWithRecords)
      } catch (error) {
        console.error('Error loading homework dates:', error)
        this.homeworkDatesWithRecords = []
      }
    },
    
    async onClassSelectedForHomework() {
      await this.loadHomeworkData()
      await this.loadHomeworkDates()
    },
    
    async loadHomeworkData() {
      try {
        this.homeworkLoading = true
        let response
        
        if (this.selectedClassForHomework && this.selectedHomeworkDate) {
          // Load homework for specific class and date
          const classStudents = this.students.filter(student => student.sinip === this.selectedClassForHomework)
          const studentIds = classStudents.map(student => student.id)
          
          // Get homework records for these students on the selected date
          const formattedDate = this.formatDateForAPI(this.selectedHomeworkDate)
          response = await attendanceApi.getTeacherHomeworkByDate(formattedDate)
          this.homeworkRecords = response.data.filter(record => 
            studentIds.includes(record.student_id)
          )
        } else if (this.selectedHomeworkDate) {
          // Load homework for all students on specific date
          const formattedDate = this.formatDateForAPI(this.selectedHomeworkDate)
          response = await attendanceApi.getTeacherHomeworkByDate(formattedDate)
          this.homeworkRecords = response.data
        } else {
          // Load all homework records
          response = await attendanceApi.getTeacherHomework()
          this.homeworkRecords = response.data
        }
        
        // Always load all records for summary table
        const allHomeworkResponse = await attendanceApi.getTeacherHomework()
        this.allHomeworkRecords = allHomeworkResponse.data
        
        this.updateHomeworkStatistics()
        this.updateHomeworkSummary()
        
        // Load dates with records for calendar highlighting
        await this.loadHomeworkDates()
      } catch (error) {
        this.showSnackbar('تاپشۇرۇق مەلۇماتى يۈكلەش خاتالىقى', 'error')
        console.error('Error loading homework data:', error)
      } finally {
        this.homeworkLoading = false
      }
    },

    updateHomeworkStatistics() {
      const totalHomework = this.homeworkRecords.length
      const completedHomework = this.homeworkRecords.filter(record => record.total_points > 0).length
      const incompleteHomework = totalHomework - completedHomework
      const averagePoints = totalHomework > 0 ? 
        (this.homeworkRecords.reduce((sum, record) => sum + parseFloat(record.total_points), 0) / totalHomework).toFixed(2) : 0

      this.homeworkStats[0].value = totalHomework
      this.homeworkStats[1].value = completedHomework
      this.homeworkStats[2].value = incompleteHomework
      this.homeworkStats[3].value = averagePoints
    },

    getStudentsForClass(classId) {
      return this.students.filter(student => student.sinip === classId)
    },

    getVisibleHomeworkFields() {
      return ['new_class_preparation', 'main_text_reading', 'reading', 'exercise', 'spelling']
        .filter(field => this.homeworkVisibleFields[field] !== false)
    },

    getHomeworkFormStudents() {
      if (!this.homeworkForm.classId) return []
      return this.getStudentsForClass(this.homeworkForm.classId)
    },

    isHomeworkFieldAllSelected(field) {
      const students = this.getHomeworkFormStudents()
      if (students.length === 0) return false
      return students.every(student => this.homeworkForm.students[student.id]?.[field])
    },

    isHomeworkFieldIndeterminate(field) {
      const students = this.getHomeworkFormStudents()
      if (students.length === 0) return false
      const selectedCount = students.filter(student => this.homeworkForm.students[student.id]?.[field]).length
      return selectedCount > 0 && selectedCount < students.length
    },

    toggleHomeworkFieldForAll(field, value) {
      this.getHomeworkFormStudents().forEach(student => {
        if (this.homeworkForm.students[student.id]) {
          this.homeworkForm.students[student.id][field] = value
          this.updateStudentPoints(student.id)
        }
      })
    },

    selectAllHomeworkForAllStudents() {
      const fields = this.getVisibleHomeworkFields()
      this.getHomeworkFormStudents().forEach(student => {
        const studentData = this.homeworkForm.students[student.id]
        if (!studentData) return
        fields.forEach(field => {
          studentData[field] = true
        })
        this.updateStudentPoints(student.id)
      })
    },

    clearAllHomeworkForAllStudents() {
      const fields = this.getVisibleHomeworkFields()
      this.getHomeworkFormStudents().forEach(student => {
        const studentData = this.homeworkForm.students[student.id]
        if (!studentData) return
        fields.forEach(field => {
          studentData[field] = false
        })
        this.updateStudentPoints(student.id)
      })
    },

    isStudentHomeworkAllSelected(studentId) {
      const fields = this.getVisibleHomeworkFields()
      if (fields.length === 0) return false
      const studentData = this.homeworkForm.students[studentId]
      if (!studentData) return false
      return fields.every(field => studentData[field])
    },

    toggleSelectAllForStudent(studentId, value) {
      const fields = this.getVisibleHomeworkFields()
      const studentData = this.homeworkForm.students[studentId]
      if (!studentData) return
      fields.forEach(field => {
        studentData[field] = value
      })
      this.updateStudentPoints(studentId)
    },

    initializeStudentHomeworkData(classId) {
      const classStudents = this.getStudentsForClass(classId)
      this.homeworkForm.students = {}
      
      classStudents.forEach(student => {
        this.homeworkForm.students[student.id] = {
          student_id: student.id,
          notes: '',
          new_class_preparation: false,
          main_text_reading: false,
          reading: false,
          exercise: false,
          spelling: false,
          total_points: 0.0
        }
      })
    },

    updateStudentPoints(studentId) {
      const studentData = this.homeworkForm.students[studentId]
      let total = 0.0
      
      if (studentData.new_class_preparation) total += this.homeworkPointValues.new_class_preparation || 0
      if (studentData.main_text_reading) total += this.homeworkPointValues.main_text_reading || 0
      if (studentData.reading) total += this.homeworkPointValues.reading || 0
      if (studentData.exercise) total += this.homeworkPointValues.exercise || 0
      if (studentData.spelling) total += this.homeworkPointValues.spelling || 0
      
      studentData.total_points = total
    },

    updateEditPoints() {
      let total = 0.0
      
      if (this.editHomeworkForm.new_class_preparation) total += this.homeworkPointValues.new_class_preparation || 0
      if (this.editHomeworkForm.main_text_reading) total += this.homeworkPointValues.main_text_reading || 0
      if (this.editHomeworkForm.reading) total += this.homeworkPointValues.reading || 0
      if (this.editHomeworkForm.exercise) total += this.homeworkPointValues.exercise || 0
      if (this.editHomeworkForm.spelling) total += this.homeworkPointValues.spelling || 0
      
      this.editHomeworkForm.total_points = total
    },

    getPointsColor(points) {
      const pointValue = parseFloat(points)
      if (pointValue >= 0.8) return 'green'
      if (pointValue >= 0.5) return 'orange'
      return 'red'
    },

    async saveHomework() {
      try {
        const homeworkRecords = []
        
        Object.keys(this.homeworkForm.students).forEach(studentId => {
          const studentData = this.homeworkForm.students[studentId]
          if (studentData.total_points > 0 || studentData.notes) {
            homeworkRecords.push({
              student_id: parseInt(studentId),
              notes: studentData.notes,
              new_class_preparation: studentData.new_class_preparation,
              main_text_reading: studentData.main_text_reading,
              reading: studentData.reading,
              exercise: studentData.exercise,
              spelling: studentData.spelling
            })
          }
        })

        if (homeworkRecords.length === 0) {
          this.showSnackbar('ھېچقانداق تاپشۇرۇق خاتىرىلەنمىدى', 'warning')
          return
        }

        const data = {
          date: this.homeworkForm.date,
          homework_records: homeworkRecords
        }

        await attendanceApi.bulkRecordHomework(data)
        this.showSnackbar('تاپشۇرۇق مۇۋەپپەقىيەتلىك ساقلاندى', 'success')
        this.closeRecordHomeworkDialog()
        await this.loadHomeworkData()
      } catch (error) {
        this.showSnackbar('تاپشۇرۇق ساقلاش خاتالىقى', 'error')
        console.error('Error saving homework:', error)
      }
    },

    editHomework(item) {
      this.editHomeworkForm = {
        id: item.id,
        notes: item.notes || '',
        new_class_preparation: item.new_class_preparation,
        main_text_reading: item.main_text_reading,
        reading: item.reading,
        exercise: item.exercise,
        spelling: item.spelling,
        total_points: parseFloat(item.total_points)
      }
      this.showEditHomeworkDialog = true
    },

    async updateHomework() {
      try {
        const data = {
          notes: this.editHomeworkForm.notes,
          new_class_preparation: this.editHomeworkForm.new_class_preparation,
          main_text_reading: this.editHomeworkForm.main_text_reading,
          reading: this.editHomeworkForm.reading,
          exercise: this.editHomeworkForm.exercise,
          spelling: this.editHomeworkForm.spelling
        }

        await attendanceApi.updateHomework(this.editHomeworkForm.id, data)
        this.showSnackbar('تاپشۇرۇق مۇۋەپپەقىيەتلىك يېڭىلاندى', 'success')
        this.showEditHomeworkDialog = false
        await this.loadHomeworkData()
      } catch (error) {
        this.showSnackbar('تاپشۇرۇق يېڭىلاش خاتالىقى', 'error')
        console.error('Error updating homework:', error)
      }
    },

    async deleteHomework(id) {
      if (confirm('بۇ تاپشۇرۇق خاتىرىسىنى ئۆچۈرەمسىز؟')) {
        try {
          await attendanceApi.deleteHomework(id)
          this.showSnackbar('تاپشۇرۇق خاتىرىسى ئۆچۈرۈلدى', 'success')
          await this.loadHomeworkData()
        } catch (error) {
          this.showSnackbar('تاپشۇرۇق ئۆچۈرۈش خاتالىقى', 'error')
          console.error('Error deleting homework:', error)
        }
      }
    },

    resetHomeworkForm() {
      this.homeworkForm = {
        classId: null,
        date: new Date().toISOString().split('T')[0],
        students: {}
      }
    },

    openRecordDialog() {
      // Pre-populate with selected class and date from main page
      this.recordForm.classId = this.selectedClass
      this.recordForm.date = this.selectedDate
      
      // Initialize student attendance data if class is selected
      if (this.recordForm.classId) {
        const classStudents = this.getStudentsForClass(this.recordForm.classId)
        classStudents.forEach(student => {
          this.studentAttendance[student.id] = 'present'
          this.studentNotes[student.id] = ''
        })
      }
      
      this.showRecordDialog = true
    },

    openRecordHomeworkDialog() {
      // Pre-populate with selected class and date from main page
      this.homeworkForm.classId = this.selectedClassForHomework
      this.homeworkForm.date = this.selectedHomeworkDate
      
      // Initialize student data if class is selected
      if (this.homeworkForm.classId) {
        this.initializeStudentHomeworkData(this.homeworkForm.classId)
      }
      
      this.showRecordHomeworkDialog = true
    },

    closeRecordDialog() {
      this.showRecordDialog = false
      // Reset form to default values
      this.recordForm = {
        classId: null,
        date: new Date().toISOString().split('T')[0]
      }
    },

    closeRecordHomeworkDialog() {
      this.showRecordHomeworkDialog = false
      // Reset form to default values
      this.resetHomeworkForm()
    },

    // Summary table methods
    updateAttendanceSummary() {
      // For grade_manager and manager, show all students; for teacher, filter by selected class
      if (this.canEdit && this.selectedClass) {
        this.attendanceSummaryStudents = this.students.filter(student => student.sinip === this.selectedClass)
      } else {
        // Show all students for grade_manager and manager
        this.attendanceSummaryStudents = this.students
      }
      
      // Get all unique dates from all attendance records, sorted chronologically
      const allDates = [...new Set(this.allAttendanceRecords.map(record => record.date))]
        .sort((a, b) => new Date(a) - new Date(b))
      
      this.attendanceSummaryRecords = allDates.map(date => ({ date }))
    },

    updateHomeworkSummary() {
      // For grade_manager and manager, show all students; for teacher, filter by selected class
      if (this.canEdit && this.selectedClassForHomework) {
        this.homeworkSummaryStudents = this.students.filter(student => student.sinip === this.selectedClassForHomework)
      } else {
        // Show all students for grade_manager and manager
        this.homeworkSummaryStudents = this.students
      }
      
      // Get all unique dates from all homework records, sorted chronologically
      const allDates = [...new Set(this.allHomeworkRecords.map(record => record.date))]
        .sort((a, b) => new Date(a) - new Date(b))
      
      this.homeworkSummaryRecords = allDates.map(date => ({ date }))
    },

    getStudentAttendanceStatus(studentId, date) {
      const record = this.allAttendanceRecords.find(r => 
        r.student_id === studentId && r.date === date
      )
      return record ? record.status : null
    },

    getStudentHomeworkPoints(studentId, date) {
      const record = this.allHomeworkRecords.find(r => 
        r.student_id === studentId && r.date === date
      )
      return record ? record.total_points : null
    },

    buildStudentScoreRows(classId, students, existingRecords) {
      const recordMap = new Map(existingRecords.map(record => [record.student_id, record]))
      return students.map(student => {
        const existing = recordMap.get(student.id)
        return {
          student_id: student.id,
          student_name: student.isim,
          student_family: student.famile,
          score: existing ? parseFloat(existing.score) : 0,
          notes: existing ? (existing.notes || '') : ''
        }
      })
    },

    updateScoreStats(stats, rows) {
      const total = rows.length
      const scored = rows.filter(row => parseFloat(row.score) > 0).length
      const average = total > 0
        ? (rows.reduce((sum, row) => sum + (parseFloat(row.score) || 0), 0) / total).toFixed(1)
        : 0
      stats[0].value = total
      stats[1].value = scored
      stats[2].value = total - scored
      stats[3].value = average
    },

    async loadVacationData() {
      if (!this.selectedClassForVacation) {
        this.vacationStudentScores = []
        return
      }
      try {
        this.vacationLoading = true
        const [studentsResponse, recordsResponse] = await Promise.all([
          attendanceApi.getTeacherStudents(this.selectedClassForVacation),
          attendanceApi.getTeacherVacationHomework({ class_id: this.selectedClassForVacation })
        ])
        const students = studentsResponse.data
        this.vacationStudentScores = this.buildStudentScoreRows(
          this.selectedClassForVacation,
          students,
          recordsResponse.data
        )
        this.updateScoreStats(this.vacationStats, this.vacationStudentScores)
      } catch (error) {
        this.showSnackbar('تەتىل تاپشۇرۇق مەلۇماتى يۈكلەش خاتالىقى', 'error')
        console.error('Error loading vacation homework:', error)
      } finally {
        this.vacationLoading = false
      }
    },

    async saveVacationScores() {
      if (!this.selectedClassForVacation) return
      try {
        this.vacationSaving = true
        const records = this.vacationStudentScores
          .filter(row => parseFloat(row.score) > 0 || row.notes)
          .map(row => ({
            student_id: row.student_id,
            score: parseFloat(row.score) || 0,
            notes: row.notes || ''
          }))
        if (records.length === 0) {
          this.showSnackbar('ھېچقانداق نومۇر ساقلانمىدى', 'warning')
          return
        }
        await attendanceApi.bulkRecordVacationHomework({
          class_id: this.selectedClassForVacation,
          records
        })
        this.showSnackbar('تەتىل تاپشۇرۇق مۇۋەپپەقىيەتلىك ساقلاندى', 'success')
        await this.loadVacationData()
      } catch (error) {
        this.showSnackbar('تەتىل تاپشۇرۇق ساقلاش خاتالىقى', 'error')
        console.error('Error saving vacation homework:', error)
      } finally {
        this.vacationSaving = false
      }
    },

    async loadExamData() {
      if (!this.selectedClassForExam) {
        this.examStudentScores = []
        return
      }
      try {
        this.examLoading = true
        const [studentsResponse, recordsResponse] = await Promise.all([
          attendanceApi.getTeacherStudents(this.selectedClassForExam),
          attendanceApi.getTeacherExamScores({ class_id: this.selectedClassForExam })
        ])
        const students = studentsResponse.data
        this.examStudentScores = this.buildStudentScoreRows(
          this.selectedClassForExam,
          students,
          recordsResponse.data
        )
        this.updateScoreStats(this.examStats, this.examStudentScores)
      } catch (error) {
        this.showSnackbar('ئىمتىھان مەلۇماتى يۈكلەش خاتالىقى', 'error')
        console.error('Error loading exam scores:', error)
      } finally {
        this.examLoading = false
      }
    },

    async saveExamScores() {
      if (!this.selectedClassForExam) return
      try {
        this.examSaving = true
        const records = this.examStudentScores
          .filter(row => parseFloat(row.score) > 0 || row.notes)
          .map(row => ({
            student_id: row.student_id,
            score: parseFloat(row.score) || 0,
            notes: row.notes || ''
          }))
        if (records.length === 0) {
          this.showSnackbar('ھېچقانداق نومۇر ساقلانمىدى', 'warning')
          return
        }
        await attendanceApi.bulkRecordExamScores({
          class_id: this.selectedClassForExam,
          records
        })
        this.showSnackbar('ئىمتىھان نۇقتىسى مۇۋەپپەقىيەتلىك ساقلاندى', 'success')
        await this.loadExamData()
      } catch (error) {
        this.showSnackbar('ئىمتىھان نۇقتىسى ساقلاش خاتالىقى', 'error')
        console.error('Error saving exam scores:', error)
      } finally {
        this.examSaving = false
      }
    },

    onGradeSelectedForSummary() {
      this.selectedClassForSummary = null
      this.summaryData = []
    },

    async loadSummaryData() {
      if (!this.selectedClassForSummary) {
        this.summaryData = []
        return
      }
      try {
        this.summaryLoading = true
        const params = { class_id: this.selectedClassForSummary }
        const response = await attendanceApi.getTeacherStudentSummary(params)
        this.summaryData = response.data
      } catch (error) {
        this.showSnackbar('خۇلاسە مەلۇماتى يۈكلەش خاتالىقى', 'error')
        console.error('Error loading summary:', error)
      } finally {
        this.summaryLoading = false
      }
    },

    formatScore(value) {
      return parseFloat(value || 0).toFixed(1)
    },

    getSummaryColor(totalScore) {
      const score = parseFloat(totalScore)
      if (score >= 80) return 'green'
      if (score >= 60) return 'orange'
      return 'red'
    },

    async confirmFinalizeSchoolYear() {
      const confirmed = confirm(
        `ئوقۇش يىلى ${this.currentSchoolYear} نى ئاخىرلاشتۇرامسىز؟\n` +
        `ھەر بىر سىنىپنىڭ يىللىق دەرىجىسى بىر دەرىجە يۇقىرىلايدۇ، يېڭى ئوقۇش يىلى ${this.currentSchoolYear + 1} بولىدۇ.`
      )
      if (!confirmed) return
      await this.finalizeSchoolYear()
    },

    async finalizeSchoolYear() {
      try {
        this.finalizingYear = true
        const response = await attendanceApi.finalizeSchoolYear()
        this.currentSchoolYear = response.data.new_year
        this.showSnackbar(response.data.message || 'ئوقۇش يىلى ئاخىرلاشتۇرۇلدى', 'success')
        await this.loadTeacherClasses()
        if (this.activeTab === 'summary' && this.selectedClassForSummary) {
          await this.loadSummaryData()
        }
      } catch (error) {
        const message = error.response?.data?.error || 'يىل ئاخىرلاشتۇرۇشتا خاتالىق'
        this.showSnackbar(message, 'error')
        console.error('Error finalizing school year:', error)
      } finally {
        this.finalizingYear = false
      }
    }
  }
}
</script>

<style scoped>
/* Calendar date highlighting */
:deep(.highlighted-date-btn) {
  color: blue !important;
  font-weight: bold !important;
  position: relative;
  background-color: rgba(33, 150, 243, 0.1) !important;
}

:deep(.highlighted-date-btn .v-btn__content) {
  color: blue !important;
  font-weight: bold !important;
}

/* Add a blue dot indicator at the bottom of dates with records */
:deep(.highlighted-date-btn::after) {
  content: "";
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: blue;
  z-index: 1;
}

@font-face {
  font-family: 'UKIJQARA';
  src: url('@/assets/font/UKIJQara.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

/* Header font styling */
:deep(.v-card-title),
:deep(.text-h1),
:deep(.text-h2),
:deep(.text-h3),
:deep(.text-h4),
:deep(.text-h5),
:deep(.text-h6),
.text-h1,
.text-h2,
.text-h3,
.text-h4,
.text-h5,
.text-h6,
h1, h2, h3, h4, h5, h6 {
  font-family: 'UKIJQARA', 'UKIJQara', 'alkatip', 'Lateef', serif !important;
}
/* Summary Table Styles */
.attendance-summary-table,
.homework-summary-table {
  overflow-x: auto;
  max-width: 100%;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.summary-table th,
.summary-table td {
  border: 1px solid #e0e0e0;
  padding: 8px 12px;
  text-align: center;
  min-width: 80px;
}

.summary-table th {
  background-color: #f5f5f5;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 1;
}

.student-col {
  min-width: 150px;
  text-align: right !important;
  background-color: #f9f9f9;
  position: sticky;
  left: 0;
  z-index: 2;
}

.record-col {
  min-width: 60px;
  max-width: 80px;
}

.student-name {
  text-align: right !important;
  background-color: #f9f9f9;
  position: sticky;
  left: 0;
  z-index: 1;
  font-weight: 500;
  color: black !important;
  text-shadow: none !important;
  text-decoration: none !important;
}

.summary-table .student-name {
  color: black !important;
  text-shadow: none !important;
  text-decoration: none !important;
}

.summary-table td.student-name {
  color: black !important;
  text-shadow: none !important;
  text-decoration: none !important;
}

.status-cell,
.points-cell {
  text-align: center;
  vertical-align: middle;
}

.no-record {
  color: #999;
  font-style: italic;
}

/* Responsive adjustments for summary tables */
@media (max-width: 768px) {
  .summary-table {
    font-size: 12px;
  }
  
  .summary-table th,
  .summary-table td {
    padding: 6px 8px;
    min-width: 60px;
  }
  
  .student-col {
    min-width: 120px;
  }
}
.uyghur {
  font-family: 'UKIJQara', 'Lateef', serif;
  direction: rtl;
}

.phone-ltr {
  direction: ltr !important;
  text-align: left !important;
}

/* Target Vuetify v-text-field input elements */
.phone-ltr >>> .v-field__input {
  direction: ltr !important;
  text-align: left !important;
}

.phone-ltr >>> input {
  direction: ltr !important;
  text-align: left !important;
}

/* Target Vuetify data table cells */
.phone-ltr >>> .v-data-table__td {
  direction: ltr !important;
  text-align: left !important;
}

.phone-ltr >>> td {
  direction: ltr !important;
  text-align: left !important;
}

/* More specific selectors for Vuetify 3 */
.v-text-field.phone-ltr .v-field__input {
  direction: ltr !important;
  text-align: left !important;
}

.v-data-table .phone-ltr {
  direction: ltr !important;
  text-align: left !important;
}

/* Wheel of Names style picker */
.picker-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.wheel-stage {
  position: relative;
  width: min(420px, 86vw);
  height: min(420px, 86vw);
  margin: 28px auto 8px;
}

.wheel-pointer {
  position: absolute;
  top: -6px;
  left: 50%;
  z-index: 5;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  border-left: 14px solid transparent;
  border-right: 14px solid transparent;
  border-top: 28px solid #222;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.35));
}

.wheel-pointer::after {
  content: '';
  position: absolute;
  top: -28px;
  left: -8px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 16px solid #ff5252;
}

.wheel-disk {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  box-shadow:
    0 0 0 8px #fff,
    0 0 0 12px #e0e0e0,
    0 16px 40px rgba(0, 0, 0, 0.28);
  transition-property: transform;
  transition-duration: 5s;
  transition-timing-function: cubic-bezier(0.12, 0.75, 0.12, 1);
  background: #f5f5f5;
}

.wheel-disk.spinning {
  pointer-events: none;
}

.wheel-disk.no-transition {
  transition: none !important;
}

.wheel-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.wheel-label {
  fill: #1a1a1a;
  font-weight: 700;
  pointer-events: none;
  user-select: none;
}

.wheel-hub-ring {
  fill: #fff;
  stroke: #ddd;
  stroke-width: 0.6;
}

.wheel-center {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 6;
  width: 88px;
  height: 88px;
  transform: translate(-50%, -50%);
  border: 0;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #ffffff 0%, #f0f0f0 45%, #dcdcdc 100%);
  box-shadow:
    0 2px 0 #bbb,
    0 8px 18px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  color: #222;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1.2;
  padding: 8px;
}

.wheel-center:hover:not(:disabled) {
  transform: translate(-50%, -50%) scale(1.04);
}

.wheel-center:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.wheel-status {
  color: #555;
  font-size: 1.05rem;
}

.wheel-instant-option {
  display: flex;
  justify-content: center;
}

.wheel-empty {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 1.2rem;
}

.winner-card {
  animation: winner-pop 0.45s ease;
}

@keyframes winner-pop {
  0% { transform: scale(0.92); opacity: 0.4; }
  60% { transform: scale(1.03); opacity: 1; }
  100% { transform: scale(1); }
}

@media (max-width: 768px) {
  .wheel-center {
    width: 72px;
    height: 72px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .wheel-center {
    width: 64px;
    height: 64px;
    font-size: 11px;
  }

  .wheel-pointer {
    border-left-width: 11px;
    border-right-width: 11px;
    border-top-width: 22px;
  }
}

</style>
